import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import { OnboardingScreen } from "./components/OnboardingScreen";
import { EmailConfirmationScreen } from "./components/EmailConfirmationScreen";
import { ModeSelectScreen } from "./components/ModeSelectScreen";
import { FeedbackScreen } from "./components/feedback/FeedbackScreen";
import { FeedbackInboxScreen } from "./components/feedback/FeedbackInboxScreen";
import { FeedbackLauncher } from "./components/feedback/FeedbackLauncher";
import { describeFeedbackOrigin } from "./feedback/feedbackNavigation";
import { FEEDBACK_INBOX_SCREEN } from "./feedback/feedbackInboxAccess";
import { StartScreen } from "./components/StartScreen";
import { QuestionScreen } from "./components/QuestionScreen";
import { CompletionScreen } from "./components/CompletionScreen";
import { ReviewScreen } from "./components/ReviewScreen";
import { CreateClientScreen } from "./components/CreateClientScreen";
import { CockpitHubScreen } from "./components/CockpitHubScreen";
import { FloatingHomeButton } from "./components/FloatingHomeButton";
import { MobileBottomNav } from "./components/navigation/MobileBottomNav";
import RequestScreen from "./components/RequestScreen";
import { useQuestionnaire } from "./hooks/useQuestionnaire";
import { CreateProjectScreen } from "./components/CreateProjectScreen";
import { MissionControlScreen } from "./components/MissionControlScreen";
import { KPIDashboardScreen } from "./components/KPIDashboardScreen";
import { PlayStoreReadinessRetired } from "./components/playStoreReadiness/PlayStoreReadinessRetired";
import { GoogleVisibilityScreen } from "./components/googleVisibility/GoogleVisibilityScreen";
import { OpenSessionScreen } from "./components/OpenSessionScreen";
import { IntelligenceScreen } from "./components/IntelligenceScreen";
import { ReportBuilderScreen } from "./components/ReportBuilderScreen";
import { ProjectAnalyticsScreen } from "./components/ProjectAnalyticsScreen";
import { AdministrationHubScreen } from "./components/admin/AdministrationHubScreen";
import { AppDesktopShell } from "./components/navigation/AppDesktopShell";
import { useIsDesktopViewport } from "./hooks/useIsDesktopViewport";
import { resolvePayrollModuleScreen } from "./navigation/payrollEntry";
import { resolveEstimateHubReturn, resolveIaReturnTarget, settingsLeaveTarget } from "./navigation/iaReturnTargets";
import {
  APP_DESKTOP_NAV_ITEMS,
  appScreenReturnLabel,
  isAppDesktopShellScreen,
} from "./helpers/appDesktopNav";
import { UpdatePasswordScreen } from "./components/UpdatePasswordScreen";
import {
  isRecoveryCallback,
  clearRecoveryUrlParams,
} from "./helpers/authHelpers";
import {
  consumeConnectedServiceOAuthReturnScreen,
  readConnectedServiceOAuthReturnParams,
} from "./connectedServices";
import { ManualTimeEntryScreen } from "./components/ManualTimeEntryScreen";
import { TimeTrackingV2Screen } from "./components/TimeTrackingV2Screen";
import { ProjectDetailScreen } from "./components/ProjectDetailScreen";
import { PayrollReportScreen } from "./components/PayrollReportScreen";
import { PayrollHomeScreen } from "./components/PayrollHomeScreen";
import { PayrollQueueScreen } from "./components/PayrollQueueScreen";
import ashleyGuideVideo from "./assets/mission-control/ashleyguide1.mp4";
import ashleyUpdatesVideo from "./assets/mission-control/ashleyupdates.mp4";
import ringPng from "./assets/mission-control/ring.png";
import AccountsPayableDashboard from "./components/accounts-payable/AccountsPayableDashboard";
import AccountsReceivableScreen from "./components/accounts-receivable/AccountsReceivableScreen";
import {
  OpsDashboardCarouselPage,
  renderOpsDashboardSummary,
} from "./components/dashboard/OpsDashboardSummaries";
import { TaskCatalogScreen } from "./components/tasks/TaskCatalogScreen";
import { PlanningScreen } from "./components/planning/PlanningScreen";
import { MaterialsScreen } from "./components/MaterialsScreen";
import { EstimateHubScreen } from "./components/EstimateHubScreen";
import { InvoiceHubScreen } from "./components/InvoiceHubScreen";
import {
  FinanceAccountsScreen,
  FinanceDashboardScreen,
  FinanceMoneyFlowScreen,
  FinanceTransactionsScreen,
  FinanceTransferScreen,
} from "./components/finance";
import { generateInvoiceFromProject } from "./helpers/invoices";
import { INVOICE_HUB_OVERDUE_FILTER } from "./helpers/invoiceHubFilters";
import {
  PROJECT_HUB_OVERDUE_TASKS,
  attentionFilterFromHubOpen,
} from "./helpers/projectHubFilters";
import {
  getAdjacentDashboardScreen,
  isDashboardCarouselScreen,
} from "./config/dashboardCarousel";
import { QuickCreateScreen } from "./components/QuickCreateScreen";
import { dispatchFinancePositionChanged } from "./helpers/financeStateEvents";
import { isMissionControlEnabled } from "./config/releaseFeatures";
import { DashboardNavigator } from "./components/DashboardNavigator";
import { PayrollPrintIsolationTest } from "./components/payroll/report/PayrollPrintIsolationTest";
import { AppProviders } from "./context/AppProviders";
import { AppScreenAccessGuard } from "./components/AppScreenAccessGuard";
import {
  canAccessMode,
  canAccessScreen,
  canAccessDashboardScreen,
  getDeniedRedirectScreen,
} from "./helpers/screenGuards";
import {
  actorCanAccessPlayStoreReadiness,
  isPlayStoreReadinessRoute,
  PLAY_STORE_READINESS_SCREEN,
  setPlayStoreReadinessRoute,
} from "./helpers/playStoreReadinessAccess";
import {
  isGoogleVisibilityRoute,
  resolveGoogleVisibilityRouteDecision,
  GOOGLE_VISIBILITY_ROUTE_DECISION,
  GOOGLE_VISIBILITY_SCREEN,
  setGoogleVisibilityRoute,
} from "./helpers/googleVisibilityAccess";
import { OrgPoliciesSync } from "./components/OrgPoliciesSync";
import {
  clearGuestSession,
  createGuestUser,
  isGuestActor,
  isGuestEntryAllowed,
  persistGuestSession,
  readGuestSession,
} from "./helpers/guestMode";
import { devLog, devWarn } from "./helpers/devLog";
import { runAuthBootstrapStep, withAuthBootstrapTimeout } from "./helpers/authBootstrap";
import { isProjectAccessibleToActor, loadDirectReportUserIds } from "./helpers/teamScope";
import {
  CAPABILITY,
  can,
  isEmployee,
  isManager,
  resolveOrgPolicies,
} from "./helpers/permissions";
import { getQuickMenuFavoriteIdsForActor } from "./helpers/quickMenuFavorites";
import {
  clearLocalCurrentUser,
  getLocalCurrentUser,
  getLocalDirectReportIds,
  hasCachedAuthenticatedUser,
  hasRestorableOfflineSession,
  hydrateOfflineBoot,
  normalizeAppSession,
  buildOfflineSessionShim,
  applyCachedSessionRestore,
  shouldShowAuthBootstrapLoading,
  saveLastScreen,
  saveLocalCurrentUser,
  saveLocalDirectReportIds,
} from "./helpers/offlineBoot";
import { resolveAuthSessionOffer } from "./helpers/authSessionOffer";
import { traceTimelogWrite } from "./helpers/timelogWriteTrace";
import {
  canAttemptSync,
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  isOffline,
  reportConnectivityFailure,
  reportConnectivitySuccess,
  subscribeConnectivity,
} from "./helpers/connectivity";
import {
  clearSessionMaterials,
  commitSessionMaterialsDurably,
  detectAbandonedSessionMaterialDrafts,
} from "./helpers/sessionMaterials";
import {
  applyQuoteLineLinkToTimelogRow,
  ensureTimelogQuoteLineLink,
  resolveTimeLogId,
} from "./helpers/projectLabourLedger";
import {
  orgScopedInsert,
  resolveOrganizationIdForQueries,
  stampOrganizationId,
} from "./helpers/orgQueryScope";
import "./helpers/installOrgResolutionE2eHooks";
import { AbandonedSessionMaterialsRecovery } from "./components/AbandonedSessionMaterialsRecovery";
import { drainPendingPictures } from "./helpers/pictureSync";
import { drainPendingTaskStatus } from "./helpers/tasks/taskStatusQueue";
import { FAVORITE_PACKAGE, readFavoriteReadyMap, refreshFavoriteProjects } from "./helpers/projectFavorites";
import { recordSuccessfulSync } from "./helpers/syncStatus";
import { fetchAndCacheMaterialsCatalog } from "./helpers/materialsCatalog";
import { fetchAndCacheActiveProjects } from "./helpers/projectsCatalog";
import { getMaterialRelationName } from "./helpers/materialVisibility";
import {
  clearScopedOfflineCaches,
  getScopedPendingTimelogs,
  persistScopedPendingTimelogs,
  reconcileOfflineCachesForOrganization,
  reconcileOfflineCachesForUser,
  resolveCacheOrganizationId,
} from "./helpers/offlineCacheScope";
import { getLocalActiveSession } from "./helpers/activeSession";
import {
  backfillLocalActiveSession,
  createSyncMutex,
  ensureMaterialIdempotencyKey,
  isAuthSyncError,
  stripTimelogQueueFields,
  syncPendingActiveSessionDeletes,
} from "./helpers/syncRecovery";
import { loadMembershipForAuthUser } from "./helpers/membershipHelpers";
import { enrichActorWithMembership } from "./helpers/membershipActorAdapter";
import { resolveOrganizationIdFromActor } from "./helpers/organizationHelpers";
import {
  MEMBERSHIP_CHANGED_EVENT,
  clearInviteTokenFromLocation,
  parseInviteTokenFromLocation,
} from "./helpers/membershipInvitations";
import {
  ASHLEY_GUIDE_OPEN_EVENT,
} from "./helpers/ashleyGuideLauncher";
import { AshleyInterfaceProvider } from "./ashley/AshleyInterfaceProvider";
import { ProductEducationProvider } from "./productEducation";
import { WhatsNewHost } from "./whatsNew/WhatsNewHost";
import { AshleyVoiceOverlay } from "./ashley/AshleyVoiceOverlay";
import { AshleyVoiceDebugPanel } from "./ashley/debug/AshleyVoiceDebugPanel";
import { useAppDispatch } from "./hooks/useAppDispatch";
import { HardwareBackProvider } from "./navigation/HardwareBackContext";
import { AppHardwareBackBridge } from "./navigation/AppHardwareBackBridge";
import {
  projectHubBackTarget,
  resolveTopLevelHubReturn,
} from "./navigation/hubReturnTarget";
import { OrganizationJoinScreen } from "./components/OrganizationJoinScreen";
import { OrgSetupScreen } from "./components/OrgSetupScreen";
import { UserPreferencesScreen } from "./components/settings/UserPreferencesScreen";
import { SettingsHubScreen } from "./components/settings/SettingsHubScreen";
import { AshleyHomeScreen } from "./ashley/home/AshleyHomeScreen";
import "./App.css";
import "./layout/desktopPage.css";
import "./layout/pageLayout.css";
import "./layout/desktopFacelift.css";

const PAYROLL_PRINT_ISOLATION_HASH = "#debug-payroll-print-isolation";

function isPayrollPrintIsolationRoute() {
  if (process.env.NODE_ENV === "production") {
    return false;
  }
  return window.location.hash === PAYROLL_PRINT_ISOLATION_HASH;
}

async function syncPendingSessionMaterials(log) {
  if (!log?.pending_materials?.length || log.materials_synced) {
    return { synced: true, error: null, log };
  }

  const keyedLog = ensureMaterialIdempotencyKey(log);
  const idempotencyKey = keyedLog.material_commit_idempotency_key;

  const timeLogId = await resolveTimeLogId(keyedLog.session_id);
  const usageDate =
    keyedLog.date_iso ||
    (keyedLog.start_time
      ? new Date(keyedLog.start_time).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10));

  const result = await commitSessionMaterialsDurably({
    sessionId: keyedLog.session_id,
    projectId: keyedLog.project_id,
    enteredBy: keyedLog.supabase_user_id || keyedLog.user_id,
    timeLogId,
    usageDate,
    lines: keyedLog.pending_materials,
    idempotencyKey,
    actor: getLocalCurrentUser(),
  });

  // Durable Persistence Rule: clear only after positive confirmation
  // (commitBatchId / verified server usage) — not merely !error.
  if (result.confirmed) {
    clearSessionMaterials(keyedLog.session_id);
    return { synced: true, error: null, log: keyedLog };
  }

  return {
    synced: false,
    error:
      result.error ||
      new Error("Materials persistence not confirmed; retaining local drafts."),
    log: keyedLog,
  };
}

const runSyncExclusive = createSyncMutex();

async function syncPendingTimeLogs() {
  return runSyncExclusive(async () => {
    try {
      const actor = getLocalCurrentUser();
      const activeOrgId = resolveCacheOrganizationId(actor);

      if (actor?.supabase_user_id && activeOrgId && canAttemptSync()) {
        const localSession = getLocalActiveSession(actor.supabase_user_id);
        const backfillResult = await backfillLocalActiveSession({
          supabase,
          actor,
          localSession,
          orgScopedInsert,
          organizationId: activeOrgId,
        });

        if (
          backfillResult.error &&
          isAuthSyncError(backfillResult.error)
        ) {
          return { authBlocked: true };
        }

        const deleteResult = await syncPendingActiveSessionDeletes({
          supabase,
          organizationId: activeOrgId,
        });

        if (deleteResult.authBlocked) {
          return { authBlocked: true };
        }

        if (deleteResult.hadFailure) {
          reportConnectivityFailure({ reason: "error" });
        }
      }

      const scopedPending = getScopedPendingTimelogs(actor);

      if (!scopedPending.length) return { syncedCount: 0 };

      devLog("SYNCING PENDING TIMELOGS:", scopedPending.length);

      const remaining = [];
      let hadNetworkFailure = false;
      let authBlocked = false;
      let syncedCount = 0;

      for (const log of scopedPending) {
        if (
          log.organization_id &&
          activeOrgId &&
          log.organization_id !== activeOrgId
        ) {
          // Preserve cross-org rows; do not drop them from the rewrite.
          remaining.push(log);
          continue;
        }

        traceTimelogWrite({
          operation: "upsert",
          path: "App.syncPendingTimeLogs",
          sessionId: log.session_id,
          phase: "attempt",
        });

        const organizationId =
          log.organization_id ||
          activeOrgId ||
          (await resolveOrganizationIdForQueries(actor, supabase));

        const timelogRow = stampOrganizationId(
          await applyQuoteLineLinkToTimelogRow(stripTimelogQueueFields(log)),
          organizationId,
        );

        const { error } = await supabase
          .from("time_logs")
          .upsert(timelogRow, { onConflict: "session_id" });

        if (error) {
          traceTimelogWrite({
            operation: "upsert",
            path: "App.syncPendingTimeLogs",
            sessionId: log.session_id,
            phase: "error",
            error,
          });
          console.error("FAILED TO SYNC SESSION:", log.session_id, error);
          remaining.push(log);
          if (isAuthSyncError(error)) {
            authBlocked = true;
          } else {
            hadNetworkFailure = true;
          }
          continue;
        }

        traceTimelogWrite({
          operation: "upsert",
          path: "App.syncPendingTimeLogs",
          sessionId: log.session_id,
          phase: "success",
        });
        devLog("SYNCED SESSION:", log.session_id);
        syncedCount += 1;

        if (log.pending_materials?.length && !log.materials_synced) {
          const {
            synced,
            error: materialError,
            log: keyedLog,
          } = await syncPendingSessionMaterials(log);

          if (!synced) {
            console.error(
              "FAILED TO SYNC SESSION MATERIALS:",
              log.session_id,
              materialError,
            );
            remaining.push({ ...keyedLog, materials_synced: false });
            if (isAuthSyncError(materialError)) {
              authBlocked = true;
            } else {
              hadNetworkFailure = true;
            }
            continue;
          }
        }
      }

      persistScopedPendingTimelogs(remaining, actor);

      if (authBlocked) {
        // Keep queue; do not mark connectivity poor for JWT/auth failures.
        return { syncedCount, authBlocked: true };
      }

      if (hadNetworkFailure) {
        reportConnectivityFailure({ reason: "error" });
      } else if (syncedCount > 0) {
        reportConnectivitySuccess();
      }

      return { syncedCount, authBlocked: false };
    } catch (err) {
      console.error("SYNC ENGINE ERROR:", err);
      if (!isAuthSyncError(err)) {
        reportConnectivityFailure({ reason: "error" });
      }
      return { error: err };
    }
  });
}


// ── Screen identifiers ─────────────────────────────────────
const SCREEN = {
  ONBOARDING: "onboarding",
  EMAIL_CONFIRMATION: "email_confirmation",
  UPDATE_PASSWORD: "update_password",
  MODE_SELECT: "mode_select",
  START: "start",
  QUESTION: "question",
  REVIEW: "review",
  COMPLETE: "complete",
  CREWPILOT: "crewpilot",
  CREATE_CLIENT: "create_client",
  CREATE_PROJECT: "create_project",
  MANUAL_TIME_ENTRY: "manual_time_entry",
  TIME_TRACKING_V2: "time_tracking_v2",
  REQUEST: "request",
  MISSION_CONTROL: "mission_control",
  KPI_DASHBOARD: "kpi_dashboard",
  OPEN_SESSION: "open_session",
  INTELLIGENCE: "intelligence",
  REPORT_BUILDER: "report_builder",
  PROJECT_ANALYTICS: "project_analytics",
  PAYROLL_REPORT: "payroll_report",
  PAYROLL_HOME: "payroll_home",
  PAYROLL_QUEUE: "payroll_queue",
  PAYROLL_PRINT_ISOLATION: "payroll_print_isolation",
  USER_MANAGEMENT: "user_management",
  USER_PREFERENCES: "user_preferences",
  SETTINGS: "settings",
  FEEDBACK: "feedback",
  FEEDBACK_INBOX: FEEDBACK_INBOX_SCREEN,
  ASHLEY_HOME: "ashley_home",
  ADMINISTRATION: "administration",
  PROJECT_DETAIL: "project_detail",
  ACCOUNTS_PAYABLE: "accounts_payable",
  ACCOUNTS_RECEIVABLE: "accounts_receivable",
  MATERIALS: "materials",
  TASKS: "tasks",
  PLANNING: "planning",
  ESTIMATE_HUB: "estimate_hub",
  INVOICE_HUB: "invoice_hub",
  ESTIMATE_PIPELINE: "estimate_pipeline",
  ORG_SETUP: "org_setup",
  FINANCE_DASHBOARD: "finance_dashboard",
  FINANCE_ACCOUNTS: "finance_accounts",
  FINANCE_TRANSACTIONS: "finance_transactions",
  FINANCE_MONEY_IN: "finance_money_in",
  FINANCE_MONEY_OUT: "finance_money_out",
  FINANCE_TRANSFER: "finance_transfer",
  FINANCIAL_POSITION: "financial_position",
  QUICK_CREATE: "quick_create",
  PLAY_STORE_READINESS: PLAY_STORE_READINESS_SCREEN,
  GOOGLE_VISIBILITY: GOOGLE_VISIBILITY_SCREEN,
};

function getInitialBootState() {
  if (isPayrollPrintIsolationRoute()) {
    return {
      screen: SCREEN.PAYROLL_PRINT_ISOLATION,
      currentUser: null,
      userName: null,
      userEmail: null,
      authLoading: false,
    };
  }

  const guestUser = readGuestSession();
  if (guestUser) {
    return {
      screen: SCREEN.KPI_DASHBOARD,
      currentUser: guestUser,
      userName: guestUser.employee_name || "Guest User",
      userEmail: "guest@local",
      directReportIds: [],
      authLoading: false,
    };
  }

  const hydrated = hydrateOfflineBoot();
  return {
    screen: hydrated.screen,
    currentUser: hydrated.currentUser,
    userName: hydrated.userName,
    userEmail: hydrated.userEmail,
    directReportIds: hydrated.directReportIds || [],
    authLoading: shouldShowAuthBootstrapLoading(hydrated),
  };
}

const INITIAL_BOOT = getInitialBootState();

export default function App() {
  const [screen, setScreen] = useState(() => INITIAL_BOOT.screen);

  const [selectedProject, setSelectedProject] = useState(null);
  const [session, setSession] = useState(() =>
    INITIAL_BOOT.currentUser
      ? normalizeAppSession(
          buildOfflineSessionShim(INITIAL_BOOT.currentUser),
          null,
        )
      : null,
  );
  const [currentUser, setCurrentUser] = useState(() => INITIAL_BOOT.currentUser);
  const [authLoading, setAuthLoading] = useState(() => INITIAL_BOOT.authLoading);
  const [authBootstrapError, setAuthBootstrapError] = useState("");
  const [prevScreen, setPrevScreen] = useState(null);
  const [userEmail, setUserEmail] = useState(() => INITIAL_BOOT.userEmail);
  const [userName, setUserName] = useState(() => INITIAL_BOOT.userName);
  const [guideBriefing, setGuideBriefing] = useState(false);
  const [startupBriefing, setStartupBriefing] = useState(false);
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState("");
  const [loginSuccessMessage, setLoginSuccessMessage] = useState("");
  const [confirmVariant, setConfirmVariant] = useState("signup");
  const [dashboardNavigatorOpen, setDashboardNavigatorOpen] = useState(false);
  const [abandonedMaterialDrafts, setAbandonedMaterialDrafts] = useState([]);
  const [projectDetailTarget, setProjectDetailTarget] = useState(null);
  // Planning keeps its place (view, date, filters) while an item opened from it is on screen.
  const planningSnapshot = useRef(null);
  const restorePlanning = useRef(false);
  useEffect(() => {
    // A Planning snapshot is only meant for the round trip Planning -> item -> Back.
    if (screen !== SCREEN.PLANNING && screen !== SCREEN.PROJECT_DETAIL) {
      restorePlanning.current = false;
    }
  }, [screen]);
  const [projectDetailReturnScreen, setProjectDetailReturnScreen] = useState(
    SCREEN.KPI_DASHBOARD,
  );
  const [timeLogsReturnScreen, setTimeLogsReturnScreen] = useState(
    SCREEN.KPI_DASHBOARD,
  );
  const [timeLogsHubKey, setTimeLogsHubKey] = useState(0);
  const [timeLogsInitialProject, setTimeLogsInitialProject] = useState(null);
  // Project → Labour Breakdown → one time log: { logId, project, returnScreen, labourView }.
  const [timeLogsEditTarget, setTimeLogsEditTarget] = useState(null);
  const [timeLogsInitialDateRangeFilter, setTimeLogsInitialDateRangeFilter] =
    useState(null);
  const [planningBackTo, setPlanningBackTo] = useState(SCREEN.MODE_SELECT);
  const isDesktopViewport = useIsDesktopViewport();

  // "More" (Mode Select) is the phone launcher. On desktop every destination is in
  // the sidebar, so any path that lands there (a legacy Back target) goes Home instead.
  useEffect(() => {
    if (isDesktopViewport && screen === SCREEN.MODE_SELECT) {
      setScreen(SCREEN.KPI_DASHBOARD);
    }
  }, [isDesktopViewport, screen]);
  const [editProjectId, setEditProjectId] = useState(null);
  const [editEstimateId, setEditEstimateId] = useState(null);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [invoiceReturnScreen, setInvoiceReturnScreen] = useState(
    SCREEN.MODE_SELECT,
  );
  const [invoiceHubInitialStatusFilter, setInvoiceHubInitialStatusFilter] =
    useState(null);
  const [invoiceHubInitialView, setInvoiceHubInitialView] = useState("hub");
  const [estimateReturnScreen, setEstimateReturnScreen] = useState(
    SCREEN.MODE_SELECT,
  );
  const [clientReturnScreen, setClientReturnScreen] = useState(
    SCREEN.KPI_DASHBOARD,
  );
  const [projectReturnScreen, setProjectReturnScreen] = useState(
    SCREEN.KPI_DASHBOARD,
  );
  const [tasksInitialView, setTasksInitialView] = useState("list");
  const [tasksReturnScreen, setTasksReturnScreen] = useState(SCREEN.MODE_SELECT);
  const [payrollReturnScreen, setPayrollReturnScreen] = useState(SCREEN.MODE_SELECT);
  const [payrollHomeReturnScreen, setPayrollHomeReturnScreen] = useState(
    SCREEN.MODE_SELECT,
  );
  const [financeReturnScreen, setFinanceReturnScreen] = useState(SCREEN.MODE_SELECT);
  const [arReturnScreen, setArReturnScreen] = useState(SCREEN.MODE_SELECT);
  const [apReturnScreen, setApReturnScreen] = useState(SCREEN.KPI_DASHBOARD);
  const [materialsInitialView, setMaterialsInitialView] = useState("list");
  const [materialsReturnScreen, setMaterialsReturnScreen] = useState(
    SCREEN.MODE_SELECT,
  );
  const [financeFlowReturnScreen, setFinanceFlowReturnScreen] = useState(
    SCREEN.FINANCE_DASHBOARD,
  );
  const [timeLogsInitialView, setTimeLogsInitialView] = useState("hub");
  const [createProjectFromEstimate, setCreateProjectFromEstimate] = useState(null);
  const [estimateHubInitialStatusFilter, setEstimateHubInitialStatusFilter] =
    useState(null);
  /** Full module vs ops summary for shared carousel screen ids. */
  const [opsModuleOverride, setOpsModuleOverride] = useState(false);
  const [adminInitialSection, setAdminInitialSection] = useState("overview");
  const [settingsInitialSection, setSettingsInitialSection] = useState("overview");
  const [settingsReturnScreen, setSettingsReturnScreen] = useState(SCREEN.KPI_DASHBOARD);
  const openSettingsRef = useRef(() => {});
  const openSettings = useCallback((section = "overview") => {
    const origin = screenRef.current;
    if (origin !== SCREEN.SETTINGS) {
      setSettingsReturnScreen(settingsLeaveTarget(origin));
    }
    setSettingsInitialSection(section || "overview");
    setScreen(SCREEN.SETTINGS);
  }, []);
  openSettingsRef.current = openSettings;
  const feedbackOriginRef = useRef(null);
  const [feedbackSourceRoute, setFeedbackSourceRoute] = useState("feedback");
  const [feedbackSourceScreen, setFeedbackSourceScreen] = useState("feedback");
  const [feedbackReturnScreen, setFeedbackReturnScreen] = useState(SCREEN.MODE_SELECT);
  const screenBeforeFeedbackRef = useRef(INITIAL_BOOT.screen);
  useEffect(() => {
    const previous = screenBeforeFeedbackRef.current;
    if (
      screen === SCREEN.MODE_SELECT &&
      previous !== SCREEN.MODE_SELECT &&
      previous !== SCREEN.FEEDBACK
    ) {
      feedbackOriginRef.current = previous;
    }
    screenBeforeFeedbackRef.current = screen;
  }, [screen]);
  const [showInviteAcceptance, setShowInviteAcceptance] = useState(
    () => !!parseInviteTokenFromLocation(),
  );
  const [projectsHubKey, setProjectsHubKey] = useState(0);
  const [projectsInitialClientId, setProjectsInitialClientId] = useState("");
  const [projectsHubAttentionFilter, setProjectsHubAttentionFilter] = useState(null);
  const [clientsHubKey, setClientsHubKey] = useState(0);
  const [createProjectInitialView, setCreateProjectInitialView] = useState("hub");
  const [createClientInitialView, setCreateClientInitialView] = useState("hub");
  const [estimateHubKey, setEstimateHubKey] = useState(0);
  const [createEstimateInitialView, setCreateEstimateInitialView] = useState("hub");
  const [pendingVoiceWorkflow, setPendingVoiceWorkflow] = useState(null);
  const [directReportIds, setDirectReportIds] = useState(
    () => INITIAL_BOOT.directReportIds || [],
  );
  const screenRef = useRef(INITIAL_BOOT.screen);
  const isPasswordRecoveryRef = useRef(false);
  const googleVisibilityInboundRef = useRef(
    typeof window !== "undefined" && isGoogleVisibilityRoute(),
  );
  const googleVisibilityOpenedRef = useRef(false);
  const [googleVisibilityRouteActive, setGoogleVisibilityRouteActive] =
    useState(() => typeof window !== "undefined" && isGoogleVisibilityRoute());
  useEffect(() => {
    const handleAshleyGuideOpen = () => {
      setGuideBriefing(true);
    };

    window.addEventListener(ASHLEY_GUIDE_OPEN_EVENT, handleAshleyGuideOpen);

    return () => {
      window.removeEventListener(ASHLEY_GUIDE_OPEN_EVENT, handleAshleyGuideOpen);
    };
  }, []);

  // Connected Services OAuth return → Preferences
  useEffect(() => {
    if (!currentUser || isGuestActor(currentUser)) {
      return;
    }
    const oauthReturn = readConnectedServiceOAuthReturnParams();
    if (!oauthReturn) {
      return;
    }
    const returnScreen = consumeConnectedServiceOAuthReturnScreen();
    if (returnScreen === "user_preferences" || returnScreen || oauthReturn) {
      openSettingsRef.current("my_connected");
    }
  }, [currentUser]);

  useEffect(() => {
    const handleAshleyBriefingStart = () => {
      setStartupBriefing(true);

      if (window.lastAshleyBriefing) {
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(
          window.lastAshleyBriefing,
        );

        const voices = window.speechSynthesis.getVoices();

        const ashleyVoice = voices.find(
          (v) => v.name === "Google UK English Female",
        );

        if (ashleyVoice) {
          utterance.voice = ashleyVoice;
        }

        utterance.rate = 1;
        utterance.pitch = 0.85;
        utterance.volume = 1;

        window.speechSynthesis.speak(utterance);
      }
    };

    window.addEventListener("ashley-briefing-start", handleAshleyBriefingStart);

    return () => {
      window.removeEventListener(
        "ashley-briefing-start",
        handleAshleyBriefingStart,
      );
    };
  }, []);
  useEffect(() => {
    devLog("CURRENT USER STATE changed", {
      hasUser: Boolean(currentUser),
      role: currentUser?.role ?? null,
    });
  }, [currentUser]);
  useEffect(() => {
    const trySyncPendingTimeLogs = async () => {
      if (currentUser?.isGuest) {
        return;
      }

      if (!canAttemptSync()) return;

      const syncResult = await syncPendingTimeLogs();
      if (syncResult?.syncedCount > 0) {
        recordSuccessfulSync(currentUser?.supabase_user_id);
      }
      try {
        await drainPendingPictures({ actor: currentUser });
      } catch (error) {
        console.error("PICTURE SYNC ERROR:", error);
      }
      try {
        await drainPendingTaskStatus({ actor: currentUser });
      } catch (error) {
        console.error("TASK STATUS SYNC ERROR:", error);
      }
      window.dispatchEvent(new Event("crewpilot-sync-status"));

      try {
        const pending = getScopedPendingTimelogs(currentUser);
        const abandoned = await detectAbandonedSessionMaterialDrafts({
          pendingTimeLogs: pending,
          supabaseUserId: currentUser?.supabase_user_id,
        });
        setAbandonedMaterialDrafts(abandoned);
      } catch (error) {
        console.error("ABANDONED MATERIAL SCAN ERROR:", error);
      }
    };

    const syncNow = () => {
      drainPendingPictures({ actor: currentUser, force: true }).catch((error) => {
        console.error("PICTURE SYNC ERROR:", error);
      });
      drainPendingTaskStatus({ actor: currentUser }).catch((error) => {
        console.error("TASK STATUS SYNC ERROR:", error);
      });
      trySyncPendingTimeLogs();
    };

    trySyncPendingTimeLogs();
    window.addEventListener("online", trySyncPendingTimeLogs);
    window.addEventListener("crewpilot-sync-now", syncNow);

    const unsubscribeConnectivity = subscribeConnectivity((nextState) => {
      if (nextState === CONNECTIVITY_STATE.GOOD) {
        trySyncPendingTimeLogs();
      }
    });

    const syncInterval = setInterval(trySyncPendingTimeLogs, 60000);

    // A failed package refresh (flaky network, expired session) is retried a
    // few times with backoff instead of waiting for the next launch/online event.
    const FAVORITE_RETRY_DELAYS_MS = [20000, 60000, 180000];
    let favoriteRetryTimer = null;
    let favoriteRetryCount = 0;
    const refreshFavorites = (fromRetry) => {
      if (!currentUser || currentUser.isGuest || !canAttemptSync()) return;
      if (fromRetry !== true) favoriteRetryCount = 0;
      clearTimeout(favoriteRetryTimer);
      refreshFavoriteProjects(currentUser)
        .then((ids) => {
          const map = readFavoriteReadyMap(currentUser);
          const stillFailing = (ids || []).some((id) => map[id] === FAVORITE_PACKAGE.ATTENTION);
          if (stillFailing && favoriteRetryCount < FAVORITE_RETRY_DELAYS_MS.length) {
            favoriteRetryTimer = setTimeout(
              () => refreshFavorites(true),
              FAVORITE_RETRY_DELAYS_MS[favoriteRetryCount++],
            );
          }
        })
        .catch((error) => {
          console.error("FAVORITE PROJECT REFRESH ERROR:", error);
        });
    };
    refreshFavorites();
    window.addEventListener("online", refreshFavorites);
    const unsubscribeFavoriteRefresh = subscribeConnectivity((nextState) => {
      if (nextState === CONNECTIVITY_STATE.GOOD) refreshFavorites();
    });

    return () => {
      window.removeEventListener("online", trySyncPendingTimeLogs);
      window.removeEventListener("online", refreshFavorites);
      window.removeEventListener("crewpilot-sync-now", syncNow);
      unsubscribeConnectivity();
      unsubscribeFavoriteRefresh();
      clearTimeout(favoriteRetryTimer);
      clearInterval(syncInterval);
    };
  }, [currentUser]);
  const [collectedAnswers, setCollectedAnswers] = useState([]);

  const {
    currentQuestion,
    questionIndex,
    isLoading,
    isComplete,
    error,
    fetchQuestion,
    submitAnswer,
    reset,
    clearError,
  } = useQuestionnaire();

  // Navigate to complete when n8n signals done
  React.useEffect(() => {
    if (isComplete) {
      setScreen(SCREEN.COMPLETE);
    }
  }, [isComplete]);

  useEffect(() => {
    screenRef.current = screen;
    saveLastScreen(screen);
  }, [screen]);

  useEffect(() => {
    let cancelled = false;

    async function loadTeamScope() {
      if (!currentUser?.supabase_user_id || !isManager(currentUser)) {
        if (!cancelled) {
          setDirectReportIds([]);
          saveLocalDirectReportIds([]);
        }
        return;
      }

      if (isOffline()) {
        const cachedIds = getLocalDirectReportIds();
        if (!cancelled) {
          setDirectReportIds(cachedIds);
        }
        return;
      }

      try {
        const ids = await loadDirectReportUserIds(
          supabase,
          currentUser.supabase_user_id,
          currentUser,
        );
        if (!cancelled) {
          setDirectReportIds(ids);
          saveLocalDirectReportIds(ids);
        }
      } catch (error) {
        console.error("DIRECT REPORT LOAD ERROR:", error);
        const cachedIds = getLocalDirectReportIds();
        if (!cancelled) {
          setDirectReportIds(cachedIds);
        }
      }
    }

    loadTeamScope();

    const handleConnectivityRestored = (event) => {
      if (event.detail?.state === CONNECTIVITY_STATE.GOOD) {
        loadTeamScope();
      }
    };

    window.addEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivityRestored);

    return () => {
      cancelled = true;
      window.removeEventListener(
        CONNECTIVITY_CHANGED_EVENT,
        handleConnectivityRestored,
      );
    };
  }, [currentUser?.supabase_user_id, currentUser?.role]);

  // Screen capability gating runs inside AppProviders via AppScreenAccessGuard
  // so org settings (e.g. modules.finance.enabled) are available.

  const openProjectDetail = useCallback(
    (project, returnScreen, target = null) => {
      const projectAssigned = isProjectAccessibleToActor(
        currentUser,
        project,
        directReportIds,
      );

      if (
        !canAccessScreen(
          currentUser,
          SCREEN.PROJECT_DETAIL,
          { projectAssigned },
          undefined,
          directReportIds,
        )
      ) {
        setScreen(getDeniedRedirectScreen(currentUser));
        return;
      }

      setSelectedProject(project);
      setProjectDetailTarget(target);
      setProjectDetailReturnScreen(returnScreen);
      setScreen(SCREEN.PROJECT_DETAIL);
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    },
    [currentUser, directReportIds],
  );

  const openProjectEdit = useCallback(
    (project) => {
      const projectAssigned = isProjectAccessibleToActor(
        currentUser,
        project,
        directReportIds,
      );

      if (
        !can(currentUser, CAPABILITY.PROJECT_EDIT) ||
        !canAccessScreen(
          currentUser,
          SCREEN.CREATE_PROJECT,
          { projectAssigned },
          undefined,
          directReportIds,
        )
      ) {
        setScreen(getDeniedRedirectScreen(currentUser));
        return;
      }

      setSelectedProject(project);
      setEditProjectId(project.project_id);
      setScreen(SCREEN.CREATE_PROJECT);
    },
    [currentUser, directReportIds],
  );

  const handleProjectEditFinished = useCallback(() => {
    setEditProjectId(null);
    setScreen(SCREEN.PROJECT_DETAIL);
  }, []);

  const openProjectsHub = useCallback((options = {}) => {
    const clientId =
      options &&
      typeof options === "object" &&
      typeof options.clientId === "string"
        ? options.clientId
        : "";
    const requestedReturn =
      options &&
      typeof options === "object" &&
      typeof options.returnScreen === "string"
        ? options.returnScreen
        : SCREEN.KPI_DASHBOARD;
    const returnScreen = resolveTopLevelHubReturn(requestedReturn, {
      dashboard: SCREEN.KPI_DASHBOARD,
      legacyCommandCenter: SCREEN.CREWPILOT,
    });
    setEditProjectId(null);
    setPendingVoiceWorkflow(null);
    setCreateProjectInitialView("hub");
    setProjectsInitialClientId(clientId);
    setProjectsHubAttentionFilter(attentionFilterFromHubOpen(options));
    setProjectReturnScreen(returnScreen);
    setProjectsHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.CREATE_PROJECT);
  }, []);

  const openProjectsWithOverdueTasks = useCallback(() => {
    openProjectsHub({
      attentionFilter: PROJECT_HUB_OVERDUE_TASKS,
      returnScreen: SCREEN.KPI_DASHBOARD,
    });
  }, [openProjectsHub]);

  const openProjectsForClient = useCallback(
    (client) => {
      openProjectsHub({
        clientId: client?.client_id || "",
        returnScreen: SCREEN.CREATE_CLIENT,
      });
    },
    [openProjectsHub],
  );

  const openTimeLogsHub = useCallback(
    (returnScreen = SCREEN.KPI_DASHBOARD, options = {}) => {
      const projectId = options.projectId || null;
      const projectName = options.projectName || null;

      setTimeLogsReturnScreen(
        resolveTopLevelHubReturn(returnScreen, {
          dashboard: SCREEN.KPI_DASHBOARD,
          legacyCommandCenter: SCREEN.CREWPILOT,
        }),
      );
      setTimeLogsInitialProject(
        projectId || projectName ? { projectId, projectName } : null,
      );
      setTimeLogsInitialDateRangeFilter(options.dateRangeFilter || null);
      setTimeLogsInitialView(options.initialView === "create" ? "create" : "hub");
      setTimeLogsEditTarget(null);
      setTimeLogsHubKey((key) => key + 1);
      setOpsModuleOverride(true);
      setScreen(SCREEN.MANUAL_TIME_ENTRY);
    },
    [],
  );

  // Labour Breakdown entry → that exact time log (by id) in the Time Log editor.
  const openTimeLogFromProject = useCallback(
    (log, { labourView = "date" } = {}) => {
      if (log?.id == null || !selectedProject) return;
      if (!canAccessScreen(currentUser, SCREEN.MANUAL_TIME_ENTRY, {}, undefined, directReportIds)) {
        return;
      }
      setTimeLogsEditTarget({
        logId: log.id,
        project: selectedProject,
        returnScreen: projectDetailReturnScreen,
        labourView,
      });
      setTimeLogsInitialProject(null);
      setTimeLogsInitialDateRangeFilter(null);
      setTimeLogsInitialView("hub");
      setTimeLogsHubKey((key) => key + 1);
      setScreen(SCREEN.MANUAL_TIME_ENTRY);
    },
    [currentUser, directReportIds, projectDetailReturnScreen, selectedProject],
  );

  // Dashboard Recent Activity entry → that exact time log (by id). With no
  // project on the target, returnFromTimeLogToProject returns to the dashboard.
  const openTimeLogFromDashboard = useCallback(
    (logId) => {
      if (logId == null || logId === "") return;
      if (!canAccessScreen(currentUser, SCREEN.MANUAL_TIME_ENTRY, {}, undefined, directReportIds)) {
        return;
      }
      setTimeLogsEditTarget({
        logId,
        project: null,
        returnScreen: SCREEN.KPI_DASHBOARD,
      });
      setTimeLogsInitialProject(null);
      setTimeLogsInitialDateRangeFilter(null);
      setTimeLogsInitialView("hub");
      setTimeLogsHubKey((key) => key + 1);
      setOpsModuleOverride(true);
      setScreen(SCREEN.MANUAL_TIME_ENTRY);
    },
    [currentUser, directReportIds],
  );

  const returnFromTimeLogToProject = useCallback(() => {
    const target = timeLogsEditTarget;
    setTimeLogsEditTarget(null);
    if (!target?.project) {
      setScreen(SCREEN.KPI_DASHBOARD);
      return;
    }
    // Remounts Project Detail on Labour Breakdown, so its labour and totals reload.
    openProjectDetail(target.project, target.returnScreen, {
      kind: "labour",
      id: String(target.logId),
      labourView: target.labourView,
    });
  }, [openProjectDetail, timeLogsEditTarget]);

  const openNewProject = useCallback((returnScreen) => {
    setEditProjectId(null);
    setProjectsInitialClientId("");
    setProjectReturnScreen(
      resolveTopLevelHubReturn(
        typeof returnScreen === "string" ? returnScreen : SCREEN.KPI_DASHBOARD,
        {
          dashboard: SCREEN.KPI_DASHBOARD,
          legacyCommandCenter: SCREEN.CREWPILOT,
        },
      ),
    );
    setCreateProjectInitialView("form");
    setProjectsHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.CREATE_PROJECT);
  }, []);

  const openClientsHub = useCallback((returnScreen = SCREEN.KPI_DASHBOARD) => {
    setPendingVoiceWorkflow(null);
    setCreateClientInitialView("hub");
    setClientReturnScreen(
      resolveTopLevelHubReturn(
        typeof returnScreen === "string" ? returnScreen : SCREEN.KPI_DASHBOARD,
        {
          dashboard: SCREEN.KPI_DASHBOARD,
          legacyCommandCenter: SCREEN.CREWPILOT,
        },
      ),
    );
    setClientsHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.CREATE_CLIENT);
  }, []);

  const openNewClient = useCallback((returnScreen) => {
    setCreateClientInitialView("form");
    setClientReturnScreen(
      resolveTopLevelHubReturn(
        typeof returnScreen === "string" ? returnScreen : SCREEN.KPI_DASHBOARD,
        {
          dashboard: SCREEN.KPI_DASHBOARD,
          legacyCommandCenter: SCREEN.CREWPILOT,
        },
      ),
    );
    setClientsHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.CREATE_CLIENT);
  }, []);

  const [orgPolicies, setOrgPolicies] = useState(() => resolveOrgPolicies());
  const handleOrgPoliciesChange = useCallback((policies) => {
    setOrgPolicies(policies || resolveOrgPolicies());
  }, []);

  useEffect(() => {
    const syncInboundGoogleVisibilityRoute = () => {
      const live = isGoogleVisibilityRoute();
      if (live) {
        googleVisibilityInboundRef.current = true;
      }
      setGoogleVisibilityRouteActive(googleVisibilityInboundRef.current || live);
    };
    window.addEventListener("hashchange", syncInboundGoogleVisibilityRoute);
    window.addEventListener("popstate", syncInboundGoogleVisibilityRoute);
    syncInboundGoogleVisibilityRoute();
    return () => {
      window.removeEventListener("hashchange", syncInboundGoogleVisibilityRoute);
      window.removeEventListener("popstate", syncInboundGoogleVisibilityRoute);
    };
  }, []);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const live = isGoogleVisibilityRoute();
      if (live) {
        googleVisibilityInboundRef.current = true;
      }
      setGoogleVisibilityRouteActive(googleVisibilityInboundRef.current || live);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [authLoading, currentUser]);

  useEffect(() => {
    setPlayStoreReadinessRoute(screen === SCREEN.PLAY_STORE_READINESS);
    if (screen === SCREEN.GOOGLE_VISIBILITY) {
      googleVisibilityOpenedRef.current = true;
      googleVisibilityInboundRef.current = false;
      setGoogleVisibilityRoute(true);
      return;
    }
    if (googleVisibilityOpenedRef.current) {
      googleVisibilityOpenedRef.current = false;
      googleVisibilityInboundRef.current = false;
      setGoogleVisibilityRouteActive(false);
      setGoogleVisibilityRoute(false);
    }
  }, [screen]);

  useEffect(() => {
    if (authLoading || !currentUser) {
      return;
    }
    if (!isPlayStoreReadinessRoute()) {
      return;
    }
    if (actorCanAccessPlayStoreReadiness(currentUser)) {
      setScreen(SCREEN.PLAY_STORE_READINESS);
      return;
    }
    setPlayStoreReadinessRoute(false);
    setScreen(getDeniedRedirectScreen(currentUser, orgPolicies));
  }, [authLoading, currentUser, orgPolicies]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    const routeActive =
      googleVisibilityRouteActive ||
      googleVisibilityInboundRef.current ||
      isGoogleVisibilityRoute();
    const decision = resolveGoogleVisibilityRouteDecision(
      currentUser,
      routeActive,
    );
    if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.WAIT) {
      return;
    }
    if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.ALLOW) {
      if (screen !== SCREEN.GOOGLE_VISIBILITY) {
        setScreen(SCREEN.GOOGLE_VISIBILITY);
      }
      return;
    }
    if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.DENY) {
      googleVisibilityInboundRef.current = false;
      setGoogleVisibilityRouteActive(false);
      setGoogleVisibilityRoute(false);
      if (screen === SCREEN.GOOGLE_VISIBILITY) {
        setScreen(getDeniedRedirectScreen(currentUser, orgPolicies));
      }
    }
  }, [
    authLoading,
    currentUser,
    googleVisibilityRouteActive,
    orgPolicies,
    screen,
  ]);

  useEffect(() => {
    const onHashChange = () => {
      if (isPlayStoreReadinessRoute()) {
        setScreen(SCREEN.PLAY_STORE_READINESS);
      }
      if (!isGoogleVisibilityRoute()) {
        return;
      }
      googleVisibilityInboundRef.current = true;
      setGoogleVisibilityRouteActive(true);
      const decision = resolveGoogleVisibilityRouteDecision(currentUser, true);
      if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.ALLOW) {
        setScreen(SCREEN.GOOGLE_VISIBILITY);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [currentUser]);

  const restoreCachedAuthenticatedUser = useCallback(() => {
    if (!hasRestorableOfflineSession()) {
      return false;
    }

    const restored = applyCachedSessionRestore();
    if (!restored) {
      return false;
    }

    devLog("OFFLINE BOOT: restored cached user");
    setCurrentUser(restored.currentUser);
    setUserName(restored.userName);
    setUserEmail(restored.userEmail);
    setSession((prev) => normalizeAppSession(restored.session, prev));
    setScreen((current) => {
      if (current === SCREEN.PAYROLL_PRINT_ISOLATION) return current;
      if (
        current === SCREEN.ONBOARDING ||
        current === SCREEN.EMAIL_CONFIRMATION
      ) {
        return SCREEN.KPI_DASHBOARD;
      }
      return current;
    });
    return true;
  }, []);

  const authSessionOffer = useMemo(
    () =>
      resolveAuthSessionOffer({
        hasLiveSession: Boolean(session?.user?.id),
      }),
    [session],
  );

  // ── Supabase Auth Session ────────────────────────────────
  useEffect(() => {
    if (isRecoveryCallback()) {
      isPasswordRecoveryRef.current = true;
      devLog("[AUTH DEBUG] Recovery ref set from URL on mount");
    }

    const getSession = async () => {
      setAuthBootstrapError("");
      try {
        const {
          data: { session },
        } = await runAuthBootstrapStep(
          () => supabase.auth.getSession(),
          "session restoration",
          { retries: 1 },
        );

        devLog("[AUTH DEBUG] getSession", {
          hasSession: !!session,
          isRecovery: isPasswordRecoveryRef.current,
        });

        if (session) {
          clearGuestSession();
          setSession((prev) => normalizeAppSession(session, prev));
          setUserEmail(session.user?.email);
          setUserName((name) => name || session.user?.email);

          if (isPasswordRecoveryRef.current) {
            devLog(
              "[AUTH DEBUG] getSession: recovery session, routing to UPDATE_PASSWORD",
            );
            setScreen(SCREEN.UPDATE_PASSWORD);
            return;
          }

          const resolved = await withAuthBootstrapTimeout(
            loadCurrentUser(session),
            "user and organization restoration",
          );
          const actor = getLocalCurrentUser();
          const inboundGoogleVisibility =
            googleVisibilityInboundRef.current || isGoogleVisibilityRoute();
          if (inboundGoogleVisibility) {
            googleVisibilityInboundRef.current = true;
          }

          setScreen((current) => {
            if (current === SCREEN.PAYROLL_PRINT_ISOLATION) return current;
            if (current === SCREEN.ORG_SETUP) return current;
            if (!resolved) return SCREEN.ORG_SETUP;
            const decision = resolveGoogleVisibilityRouteDecision(
              actor,
              inboundGoogleVisibility,
            );
            if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.ALLOW) {
              return SCREEN.GOOGLE_VISIBILITY;
            }
            return current === SCREEN.ONBOARDING ||
              current === SCREEN.EMAIL_CONFIRMATION
              ? SCREEN.KPI_DASHBOARD
              : current;
          });
        } else if (restoreCachedAuthenticatedUser()) {
          // cached session restored
        } else if (readGuestSession()) {
          const guestUser = readGuestSession();
          setCurrentUser(guestUser);
          setUserName(guestUser.employee_name || "Guest User");
          setUserEmail("guest@local");
        }
      } catch (error) {
        devWarn("[AUTH BOOT] restoration failed", {
          name: error?.name,
          code: error?.code,
          message: error?.message,
        });
        if (!restoreCachedAuthenticatedUser()) {
          setAuthBootstrapError(
            "CrewPilot could not restore your session. Check your connection and retry.",
          );
        }
      } finally {
        setAuthLoading(false);
      }
    };
    const loadCurrentUser = async (session) => {
      if (!session?.user?.id) return false;

      const applyResolvedUser = async (enrichedUser) => {
        setCurrentUser(enrichedUser);
        setUserName(enrichedUser.employee_name);
        reconcileOfflineCachesForUser(enrichedUser);
        reconcileOfflineCachesForOrganization(enrichedUser);
        saveLocalCurrentUser(enrichedUser);
        fetchAndCacheMaterialsCatalog(supabase, {
          relation: getMaterialRelationName(enrichedUser),
          actor: enrichedUser,
        });
        // Hydrate CP_PROJECTS at login so Start Session works offline
        // without a prior Time Tracking visit. Authentication remains valid if
        // catalogue hydration fails; preserve the prior scoped cache and report
        // the operational-data failure separately.
        const projectHydration = await fetchAndCacheActiveProjects(
          supabase,
          enrichedUser,
          { directReportIds: getLocalDirectReportIds() },
        );
        if (projectHydration.error) {
          devWarn("[AUTH BOOT] project catalogue hydration failed", {
            code: projectHydration.error?.code,
            message: projectHydration.error?.message,
          });
        } else {
          devLog("[AUTH BOOT] project catalogue hydrated", {
            projectCount: projectHydration.projects?.length || 0,
          });
        }
        return true;
      };

      const { data: userRow, error } = await supabase
        .from("users")
        .select(
          `
  employee_name,
  supabase_user_id,
  role,
  manager_supabase_user_id,
  phone,
  email,
  classification_name,
  hourly_rate,
  payroll_type,
  payroll_access,
  finance_access,
  trade_id,
  trades ( trade_name )
  `,
        )
        .eq("supabase_user_id", session.user.id)
        .single();

      devLog("LOAD USER ERROR:", error);
      devLog("LOAD USER ROW:", userRow);

      if (userRow) {
        const normalizedUser = {
          ...userRow,
          trade_name: userRow.trade_name || userRow.trades?.trade_name || null,
        };

        let enrichedUser = normalizedUser;
        try {
          const membershipBundle = await loadMembershipForAuthUser(session.user.id);
          enrichedUser = enrichActorWithMembership(normalizedUser, membershipBundle);
        } catch (membershipErr) {
          devWarn(
            "[loadCurrentUser] membership enrich skipped — legacy users path",
            membershipErr,
          );
        }

        return applyResolvedUser(enrichedUser);
      }

      try {
        const membershipBundle = await loadMembershipForAuthUser(session.user.id);
        if (membershipBundle.membership) {
          const enrichedUser = enrichActorWithMembership(
            {
              supabase_user_id: session.user.id,
              employee_name:
                membershipBundle.membership.employee_name ||
                session.user.email,
              role: "employee",
              email: session.user.email,
            },
            membershipBundle,
          );
          return applyResolvedUser(enrichedUser);
        }
      } catch (membershipErr) {
        devWarn(
          "[loadCurrentUser] membership-only path failed",
          membershipErr,
        );
      }

      const cachedUser = getLocalCurrentUser();
      if (cachedUser?.supabase_user_id === session.user.id) {
        devLog("USING CACHED USER:", cachedUser);
        setCurrentUser(cachedUser);
        setUserName(cachedUser.employee_name);
        return true;
      }

      setCurrentUser(null);
      setScreen(SCREEN.ORG_SETUP);
      return false;
    };
    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      devLog("[AUTH DEBUG] onAuthStateChange", {
        event,
        hasSession: !!session,
        isRecoveryRef: isPasswordRecoveryRef.current,
      });

      if (session) {
        clearGuestSession();
        setSession((prev) => normalizeAppSession(session, prev));
        setUserEmail(session.user?.email);

        const isRecovery =
          event === "PASSWORD_RECOVERY" ||
          (event === "INITIAL_SESSION" && isRecoveryCallback()) ||
          isPasswordRecoveryRef.current;

        if (isRecovery) {
          isPasswordRecoveryRef.current = true;
          devLog("[AUTH DEBUG] Routing to UPDATE_PASSWORD", { event });
          setScreen(SCREEN.UPDATE_PASSWORD);
          return;
        }

        loadCurrentUser(session).then((resolved) => {
          const current = screenRef.current;
          if (current === SCREEN.ORG_SETUP) {
            return;
          }
          if (
            resolved &&
            (current === SCREEN.ONBOARDING ||
              current === SCREEN.EMAIL_CONFIRMATION)
          ) {
            setScreen(SCREEN.KPI_DASHBOARD);
          } else if (!resolved) {
            setScreen(SCREEN.ORG_SETUP);
          }
        });
        return;
      }

      const current = screenRef.current;
      if (current === SCREEN.UPDATE_PASSWORD) {
        return;
      }

      if (event === "SIGNED_OUT") {
        clearLocalCurrentUser();
        clearScopedOfflineCaches();
        setCurrentUser(null);
        setSession(null);
        setScreen(SCREEN.ONBOARDING);
        return;
      }

      if (hasRestorableOfflineSession()) {
        devLog(
          "[AUTH DEBUG] Session unavailable — keeping offline cached user",
          { event },
        );
        restoreCachedAuthenticatedUser();
        return;
      }

      if (readGuestSession()) {
        return;
      }

      setSession(null);
      setScreen(SCREEN.ONBOARDING);
    });

    const handleMembershipChanged = () => {
      supabase.auth.getSession().then(({ data: { session: activeSession } }) => {
        if (activeSession) {
          loadCurrentUser(activeSession);
        }
      });
    };

    window.addEventListener(MEMBERSHIP_CHANGED_EVENT, handleMembershipChanged);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(MEMBERSHIP_CHANGED_EVENT, handleMembershipChanged);
    };
  }, [restoreCachedAuthenticatedUser]);

  // ── Onboarding ────────────────────────────────────────────
  const handleSignupResult = ({ email, session, user }) => {
    const isDuplicateSignup =
      user && Array.isArray(user.identities) && user.identities.length === 0;

    if (isDuplicateSignup) {
      setPendingConfirmEmail(email);
      setConfirmVariant("duplicate");
      setScreen(SCREEN.EMAIL_CONFIRMATION);
      return;
    }

    if (!session) {
      setPendingConfirmEmail(email);
      setConfirmVariant("signup");
      setScreen(SCREEN.EMAIL_CONFIRMATION);
      return;
    }

    setScreen(SCREEN.KPI_DASHBOARD);
  };

  const handleNeedsEmailConfirmation = (email) => {
    setPendingConfirmEmail(email);
    setConfirmVariant("unverified");
    setScreen(SCREEN.EMAIL_CONFIRMATION);
  };

  const handleBackToSignIn = () => {
    setScreen(SCREEN.ONBOARDING);
  };

  const handlePasswordUpdateComplete = async () => {
    devLog("[AUTH DEBUG] handlePasswordUpdateComplete");
    const emailForLogin = userEmail || "";
    isPasswordRecoveryRef.current = false;
    clearRecoveryUrlParams();
    clearLocalCurrentUser();
    setCurrentUser(null);
    setLoginSuccessMessage("Your password has been updated. Please sign in.");
    setPendingConfirmEmail(emailForLogin);

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("[AUTH DEBUG] signOut after password update:", error);
    }

    setScreen(SCREEN.ONBOARDING);
  };

  const handlePasswordRecoveryBackToSignIn = async () => {
    devLog("[AUTH DEBUG] handlePasswordRecoveryBackToSignIn");
    isPasswordRecoveryRef.current = false;
    clearRecoveryUrlParams();
    clearLocalCurrentUser();
    setCurrentUser(null);

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("[AUTH DEBUG] signOut from recovery back:", error);
    }

    setScreen(SCREEN.ONBOARDING);
  };

  const handleOnboardingContinue = ({ email, isGuest }) => {
    if (isGuest) {
      if (!isGuestEntryAllowed(orgPolicies)) {
        window.alert(
          "Guest demo mode is not available. Sign in with your organization account.",
        );
        return;
      }

      const guestUser = createGuestUser();

      setCurrentUser(guestUser);
      setUserName(guestUser.employee_name);
      setUserEmail("guest@local");
      persistGuestSession(guestUser);
      setScreen(SCREEN.KPI_DASHBOARD);
      return;
    }

    setUserEmail(email);
    setScreen(SCREEN.KPI_DASHBOARD);
  };

  const handleOrgSetupComplete = () => {
    window.dispatchEvent(new CustomEvent(MEMBERSHIP_CHANGED_EVENT));
    setScreen(SCREEN.KPI_DASHBOARD);
  };

  const handleSignOutFromOrgSetup = async () => {
    clearLocalCurrentUser();
    await supabase.auth.signOut();
    setCurrentUser(null);
    setSession(null);
    setScreen(SCREEN.ONBOARDING);
  };

  const openInvoiceHub = useCallback(({
    editInvoiceId = null,
    statusFilter = null,
    initialView = "hub",
    returnScreen = SCREEN.MODE_SELECT,
  } = {}) => {
    setEditInvoiceId(editInvoiceId);
    setInvoiceHubInitialStatusFilter(statusFilter);
    setInvoiceHubInitialView(initialView === "create" ? "create" : "hub");
    setInvoiceReturnScreen(resolveIaReturnTarget(returnScreen));
    setScreen(SCREEN.INVOICE_HUB);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  // ── Mode select ───────────────────────────────────────────
  const openFeedback = () => {
    const current = screenRef.current;
    if (current === SCREEN.FEEDBACK) return;
    const origin = describeFeedbackOrigin({
      currentScreen: current,
      moreOrigin: feedbackOriginRef.current,
    });
    setFeedbackReturnScreen(origin.returnScreen);
    setFeedbackSourceRoute(origin.sourceRoute);
    setFeedbackSourceScreen(origin.sourceScreen);
    setScreen(SCREEN.FEEDBACK);
  };

  const closeFeedback = () => {
    const target = feedbackReturnScreen;
    if (isDesktopViewport && target === SCREEN.MODE_SELECT) {
      setScreen(SCREEN.KPI_DASHBOARD);
      return;
    }
    setScreen(target || SCREEN.MODE_SELECT);
  };

  const handleModeSelect = (mode) => {
    if (mode === "feedback") {
      openFeedback();
      return;
    }
    if (mode === "feedback_inbox") {
      if (!canAccessMode(currentUser, mode, orgPolicies)) {
        setScreen(getDeniedRedirectScreen(currentUser, orgPolicies));
        return;
      }
      setScreen(SCREEN.FEEDBACK_INBOX);
      return;
    }
    if (!canAccessMode(currentUser, mode, orgPolicies)) {
      setScreen(getDeniedRedirectScreen(currentUser, orgPolicies));
      return;
    }

    if (mode === "intelligence") {
      setScreen(SCREEN.INTELLIGENCE);
    } else if (mode === "mission") {
      setScreen(SCREEN.MISSION_CONTROL);
    } else if (mode === "user_management" || mode === "administration") {
      setAdminInitialSection(mode === "user_management" ? "users" : "overview");
      setScreen(SCREEN.ADMINISTRATION);
    } else if (mode === "play_store_readiness") {
      setScreen(SCREEN.PLAY_STORE_READINESS);
    } else if (mode === "google_visibility") {
      setScreen(SCREEN.GOOGLE_VISIBILITY);
    } else if (mode === "finance") {
      setFinanceReturnScreen(resolveIaReturnTarget("more"));
      setScreen(SCREEN.FINANCE_DASHBOARD);
    } else if (mode === "payroll") {
      const payrollScreen = resolvePayrollModuleScreen(isDesktopViewport);
      const payrollParent = resolveIaReturnTarget("more");
      setPayrollHomeReturnScreen(payrollParent);
      setPayrollReturnScreen(payrollParent);
      setScreen(payrollScreen);
    } else if (mode === "accounts_payable") {
      setApReturnScreen(resolveIaReturnTarget("more"));
      setOpsModuleOverride(true);
      setScreen(SCREEN.ACCOUNTS_PAYABLE);
    } else if (mode === "accounts_receivable") {
      setArReturnScreen(resolveIaReturnTarget("more"));
      setOpsModuleOverride(true);
      setScreen(SCREEN.ACCOUNTS_RECEIVABLE);
    } else if (mode === "clients") {
      openClientsHub(SCREEN.MODE_SELECT);
    } else if (mode === "materials") {
      setMaterialsInitialView("list");
      setMaterialsReturnScreen(SCREEN.MODE_SELECT);
      setScreen(SCREEN.MATERIALS);
    } else if (mode === "tasks") {
      setTasksInitialView("list");
      setTasksReturnScreen(SCREEN.MODE_SELECT);
      setScreen(SCREEN.TASKS);
    } else if (mode === "planning") {
      setPlanningBackTo(SCREEN.MODE_SELECT);
      setScreen(SCREEN.PLANNING);
    } else if (mode === "manual_time_entry") {
      openTimeLogsHub(SCREEN.MODE_SELECT);
    } else if (mode === "estimates") {
      openEstimateHub("more");
    } else if (mode === "invoices") {
      openInvoiceHub({ returnScreen: SCREEN.MODE_SELECT });
    } else {
      setScreen(SCREEN.CREWPILOT);
    }
  };

  const handleAppDesktopNav = (navId) => {
    if (navId === "kpi_dashboard") {
      setScreen(SCREEN.KPI_DASHBOARD);
      return;
    }
    if (navId === "create_client") {
      openClientsHub();
      return;
    }
    if (navId === "create_project") {
      openProjectsHub();
      return;
    }
    if (navId === "manual_time_entry") {
      openTimeLogsHub(SCREEN.KPI_DASHBOARD);
      return;
    }
    if (navId === "play_store_readiness") {
      setScreen(SCREEN.PLAY_STORE_READINESS);
      return;
    }
    if (navId === "google_visibility") {
      setScreen(SCREEN.GOOGLE_VISIBILITY);
      return;
    }
    if (navId === "settings") {
      openSettings("overview");
      return;
    }
    if (navId === "feedback") {
      openFeedback();
      return;
    }
    if (navId === "feedback_inbox") {
      setScreen(SCREEN.FEEDBACK_INBOX);
      return;
    }

    const item = APP_DESKTOP_NAV_ITEMS.find((entry) => entry.id === navId);
    if (item?.mode) {
      handleModeSelect(item.mode);
    }
  };

  const handleGenerateInvoiceFromProject = useCallback(
    async (projectId) => {
      const invoice = await generateInvoiceFromProject(
        currentUser,
        projectId,
        currentUser?.id,
      );
      openInvoiceHub({
        editInvoiceId: invoice.invoice_id,
        returnScreen: projectDetailReturnScreen,
      });
    },
    [currentUser?.id, openInvoiceHub, projectDetailReturnScreen],
  );

  const handleOpenInvoiceHubWithStatus = useCallback((status) => {
    openInvoiceHub({
      statusFilter: status || null,
      returnScreen: SCREEN.FINANCE_DASHBOARD,
    });
  }, [openInvoiceHub]);

  const handleOpenInvoiceInHub = useCallback((invoice, returnScreen) => {
    const invoiceId = invoice?.invoice_id || invoice?.id || null;
    if (!invoiceId) {
      return;
    }
    openInvoiceHub({
      editInvoiceId: invoiceId,
      returnScreen: returnScreen || SCREEN.MODE_SELECT,
    });
  }, [openInvoiceHub]);

  const openFinanceWorkspace = useCallback((workspaceId) => {
    const financeParent = resolveIaReturnTarget("finance");
    if (workspaceId === "invoices") {
      openInvoiceHub({ returnScreen: financeParent });
      return;
    }
    if (workspaceId === "receivables") {
      setArReturnScreen(financeParent);
      setOpsModuleOverride(true);
      setScreen(SCREEN.ACCOUNTS_RECEIVABLE);
      return;
    }
    if (workspaceId === "payables") {
      setApReturnScreen(financeParent);
      setOpsModuleOverride(true);
      setScreen(SCREEN.ACCOUNTS_PAYABLE);
      return;
    }
    if (workspaceId === "payroll") {
      const payrollScreen = resolvePayrollModuleScreen(isDesktopViewport);
      setPayrollHomeReturnScreen(financeParent);
      setPayrollReturnScreen(financeParent);
      setScreen(payrollScreen);
    }
  }, [isDesktopViewport, openInvoiceHub]);

  const openEstimateHub = useCallback((returnScreen) => {
    setEditEstimateId(null);
    setEstimateHubInitialStatusFilter(null);
    setEstimateReturnScreen(resolveEstimateHubReturn(returnScreen, "more"));
    setCreateEstimateInitialView("hub");
    setEstimateHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.ESTIMATE_HUB);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const openNewEstimate = useCallback((returnScreen) => {
    setEditEstimateId(null);
    setEstimateHubInitialStatusFilter(null);
    setEstimateReturnScreen(
      resolveEstimateHubReturn(
        typeof returnScreen === "string" ? returnScreen : "more",
        "more",
      ),
    );
    setCreateEstimateInitialView("create");
    setEstimateHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.ESTIMATE_HUB);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const handleOpenEstimateHubWithStatus = useCallback((status, returnScreen) => {
    setEditEstimateId(null);
    setEstimateHubInitialStatusFilter(status || null);
    setEstimateReturnScreen(
      resolveEstimateHubReturn(returnScreen, SCREEN.KPI_DASHBOARD),
    );
    setCreateEstimateInitialView("hub");
    setEstimateHubKey((key) => key + 1);
    setOpsModuleOverride(true);
    setScreen(SCREEN.ESTIMATE_HUB);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const openAccountsPayableModule = useCallback((returnScreen = SCREEN.KPI_DASHBOARD) => {
    setApReturnScreen(
      resolveIaReturnTarget(
        typeof returnScreen === "string" ? returnScreen : SCREEN.KPI_DASHBOARD,
      ),
    );
    setOpsModuleOverride(true);
    setScreen(SCREEN.ACCOUNTS_PAYABLE);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const openAccountsReceivableFromOps = useCallback(() => {
    openInvoiceHub({
      statusFilter: "sent",
      returnScreen: SCREEN.KPI_DASHBOARD,
    });
  }, [openInvoiceHub]);

  const openOverdueInvoices = useCallback(() => {
    openInvoiceHub({
      statusFilter: INVOICE_HUB_OVERDUE_FILTER,
      returnScreen: SCREEN.KPI_DASHBOARD,
    });
  }, [openInvoiceHub]);

  const handleSignOut = useCallback(async () => {
    if (isGuestActor(currentUser)) {
      clearGuestSession();
      setCurrentUser(null);
      setSession(null);
      setUserName(null);
      setUserEmail(null);
      setScreen(SCREEN.ONBOARDING);
      return;
    }

    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error("Sign out error:", error.message);
    }
  }, [currentUser]);

  const appDispatch = useAppDispatch({
    currentUser,
    orgPolicies,
    userDisplayName: userName,
    directReportIds,
    setScreen,
    screenConstants: SCREEN,
    setAdminInitialSection,
    setSettingsInitialSection,
    openSettings,
    openProjectsHub,
    openNewProject,
    openClientsHub,
    openNewClient,
    openEstimateHub,
    openNewEstimate,
    setPendingVoiceWorkflow,
    handleSignOut,
  });

  const handleVoiceWorkflowConsumed = useCallback(() => {
    setPendingVoiceWorkflow(null);
  }, []);

  const prepareCarouselHubScreen = useCallback((targetScreen) => {
    setOpsModuleOverride(false);

    if (targetScreen === SCREEN.CREATE_CLIENT) {
      setCreateClientInitialView("hub");
      setClientsHubKey((key) => key + 1);
      return;
    }

    if (targetScreen === SCREEN.CREATE_PROJECT) {
      setEditProjectId(null);
      setCreateProjectFromEstimate(null);
      setCreateProjectInitialView("hub");
      setProjectsHubKey((key) => key + 1);
      return;
    }

    if (targetScreen === SCREEN.MANUAL_TIME_ENTRY) {
      setTimeLogsReturnScreen(SCREEN.KPI_DASHBOARD);
      setTimeLogsInitialProject(null);
      setTimeLogsEditTarget(null);
      setTimeLogsHubKey((key) => key + 1);
    }
  }, []);

  const handleDashboardCarouselNavigate = useCallback(
    (direction) => {
      if (screen !== SCREEN.KPI_DASHBOARD && screen !== SCREEN.QUICK_CREATE) {
        return;
      }

      const next = getAdjacentDashboardScreen(screen, direction);
      if (!next || !canAccessScreen(currentUser, next, {}, orgPolicies)) {
        return;
      }

      setScreen(next);
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    },
    [screen, currentUser, orgPolicies],
  );

  const handleQuickCreateAction = useCallback((actionId) => {
    switch (actionId) {
      case "new_client":
        openNewClient(SCREEN.QUICK_CREATE);
        break;
      case "new_estimate":
        openNewEstimate(SCREEN.QUICK_CREATE);
        break;
      case "new_project":
        openNewProject(SCREEN.QUICK_CREATE);
        break;
      case "new_task":
        setTasksInitialView("new");
        setTasksReturnScreen(SCREEN.QUICK_CREATE);
        setScreen(SCREEN.TASKS);
        break;
      case "new_invoice":
        openInvoiceHub({
          initialView: "create",
          returnScreen: SCREEN.QUICK_CREATE,
        });
        break;
      case "record_payment":
        openInvoiceHub({
          statusFilter: "sent",
          returnScreen: SCREEN.QUICK_CREATE,
        });
        break;
      case "money_out":
        setFinanceFlowReturnScreen(SCREEN.QUICK_CREATE);
        setScreen(SCREEN.FINANCE_MONEY_OUT);
        break;
      case "new_time":
        openTimeLogsHub(SCREEN.QUICK_CREATE, { initialView: "create" });
        break;
      case "new_material":
        setMaterialsInitialView("create");
        setMaterialsReturnScreen(SCREEN.QUICK_CREATE);
        setScreen(SCREEN.MATERIALS);
        break;
      default:
        break;
    }
  }, [
    openInvoiceHub,
    openNewClient,
    openNewEstimate,
    openNewProject,
    openTimeLogsHub,
  ]);

  const handleOpenDashboardNavigator = useCallback(() => {
    setDashboardNavigatorOpen(true);
  }, []);

  const handleCloseDashboardNavigator = useCallback(() => {
    setDashboardNavigatorOpen(false);
  }, []);

  const handleDashboardDirectNavigate = useCallback((targetScreen) => {
    if (!canAccessDashboardScreen(currentUser, targetScreen, orgPolicies)) {
      setScreen(getDeniedRedirectScreen(currentUser, orgPolicies));
      setDashboardNavigatorOpen(false);
      return;
    }

    prepareCarouselHubScreen(targetScreen);
    setScreen(targetScreen);
    setDashboardNavigatorOpen(false);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [currentUser, orgPolicies, prepareCarouselHubScreen]);

  useEffect(() => {
    if (!isDashboardCarouselScreen(screen)) {
      setDashboardNavigatorOpen(false);
    }
  }, [screen]);

  useEffect(() => {
    if (screen === SCREEN.MISSION_CONTROL && !isMissionControlEnabled()) {
      setScreen(SCREEN.KPI_DASHBOARD);
    }
  }, [screen]);

  // ── Start questionnaire ───────────────────────────────────
  const handleStart = async () => {
    setCollectedAnswers([]);
    await fetchQuestion();
    setScreen(SCREEN.QUESTION);
  };

  // ── Submit answer (tracks Q&A, calls webhook) ─────────────
  const handleSubmitAnswer = useCallback(
    async (answer) => {
      if (currentQuestion) {
        setCollectedAnswers((prev) => [
          ...prev,
          {
            questionId: currentQuestion.id,
            question: currentQuestion.text,
            answer,
          },
        ]);
      }
      await submitAnswer(answer);
    },
    [currentQuestion, submitAnswer],
  );

  // ── Review Answers ────────────────────────────────────────
  const handleOpenReview = () => {
    setPrevScreen(screen);
    setScreen(SCREEN.REVIEW);
  };

  const handleCloseReview = () => {
    setScreen(prevScreen || SCREEN.QUESTION);
    setPrevScreen(null);
  };

  // Scaffold: future re-record flow entry point
  const handleEditAnswer = (item, index) => {
    handleCloseReview();
  };

  // ── Workflow navigation ───────────────────────────────────
  // Home: always returns to mode select, resets session
  const handleNavHome = () => {
    reset();
    setCollectedAnswers([]);
    setScreen(SCREEN.KPI_DASHBOARD);
  };

  // Back from START → mode select
  // Back from QUESTION → mode select (frontend-only; session rewind future)
  const handleNavBack = () => {
    if (screen === SCREEN.QUESTION || screen === SCREEN.COMPLETE) {
      reset();
      setCollectedAnswers([]);
    }
    setScreen(SCREEN.MODE_SELECT);
  };

  const handleAppWorkflowBack = useCallback(
    (currentScreen) => {
      if (currentScreen === SCREEN.PROJECT_DETAIL) {
        setScreen(projectDetailReturnScreen);
        return;
      }

      if (currentScreen === SCREEN.REVIEW) {
        handleCloseReview();
        return;
      }

      if (
        currentScreen === SCREEN.QUESTION ||
        currentScreen === SCREEN.COMPLETE
      ) {
        handleNavBack();
        return;
      }

      if (currentScreen === SCREEN.START) {
        setScreen(SCREEN.MODE_SELECT);
        return;
      }

      if (currentScreen === SCREEN.USER_PREFERENCES) {
        setScreen(SCREEN.KPI_DASHBOARD);
        return;
      }

      if (currentScreen === SCREEN.SETTINGS) {
        setScreen(settingsReturnScreen || SCREEN.KPI_DASHBOARD);
        return;
      }

      if (
        currentScreen === SCREEN.FINANCE_ACCOUNTS ||
        currentScreen === SCREEN.FINANCE_TRANSACTIONS ||
        currentScreen === SCREEN.FINANCE_MONEY_IN ||
        currentScreen === SCREEN.FINANCE_MONEY_OUT ||
        currentScreen === SCREEN.FINANCE_TRANSFER
      ) {
        setScreen(SCREEN.FINANCE_DASHBOARD);
      }
    },
    [handleNavBack, projectDetailReturnScreen, settingsReturnScreen],
  );

  const hardwareBackOverlays = useMemo(
    () => ({
      dashboardNavigatorOpen,
      onCloseDashboardNavigator: handleCloseDashboardNavigator,
      guideBriefing,
      onCloseGuideBriefing: () => setGuideBriefing(false),
      startupBriefing,
      onCloseStartupBriefing: () => {
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
        setStartupBriefing(false);
      },
    }),
    [
      dashboardNavigatorOpen,
      guideBriefing,
      startupBriefing,
      handleCloseDashboardNavigator,
    ],
  );

  // ── Full restart ──────────────────────────────────────────
  const handleRestart = () => {
    reset();
    setCollectedAnswers([]);
    setScreen(SCREEN.MODE_SELECT);
  };

  if (showInviteAcceptance && session && !authLoading) {
    return (
      <AppProviders actor={currentUser}>
        <OrganizationJoinScreen
          onBack={() => {
            setShowInviteAcceptance(false);
            clearInviteTokenFromLocation();
          }}
          onComplete={() => {
            setShowInviteAcceptance(false);
            clearInviteTokenFromLocation();
          }}
        />
      </AppProviders>
    );
  }

  if (authBootstrapError && !currentUser) {
    return (
      <div className="app nx-app">
        <div style={{ color: "white", padding: "40px" }}>
          <p>{authBootstrapError}</p>
          <button type="button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (authLoading) {
    const loadingMessage = hasCachedAuthenticatedUser()
      ? "Restoring your session..."
      : "Loading CrewPilot...";

    return (
      <div className="app nx-app">
        <div style={{ color: "white", padding: "40px" }}>
          {loadingMessage}
        </div>
      </div>
    );
  }
  return (
    <AppProviders actor={currentUser} directReportIds={directReportIds}>
    <OrgPoliciesSync onChange={handleOrgPoliciesChange} />
    <AppScreenAccessGuard
      screen={screen}
      currentUser={currentUser}
      directReportIds={directReportIds}
      selectedProject={selectedProject}
      onDenied={(deniedScreen) => setScreen(deniedScreen)}
    />
    <HardwareBackProvider>
    <ProductEducationProvider
      actor={currentUser}
      screen={screen}
      authLoading={authLoading}
      onOpenUserPreferences={() => openSettings("overview")}
    >
    <AppHardwareBackBridge
      screen={screen}
      onNavigateToKpi={() => setScreen(SCREEN.KPI_DASHBOARD)}
      onAppWorkflowBack={handleAppWorkflowBack}
      overlays={hardwareBackOverlays}
    />
    <AshleyInterfaceProvider appDispatch={appDispatch} userDisplayName={userName}>
    <div className="app nx-app">
      <AppDesktopShell
        enabled={isAppDesktopShellScreen(screen)}
        screen={screen}
        onNavigate={handleAppDesktopNav}
        userName={userName}
        onOpenSettings={() => openSettings("overview")}
      >
      {screen === SCREEN.ONBOARDING && (
        <OnboardingScreen
          onContinue={handleOnboardingContinue}
          onSignupResult={handleSignupResult}
          onNeedsEmailConfirmation={handleNeedsEmailConfirmation}
          initialEmail={pendingConfirmEmail}
          initialShowEmailForm={Boolean(pendingConfirmEmail)}
          loginSuccessMessage={loginSuccessMessage}
          onClearLoginSuccessMessage={() => setLoginSuccessMessage("")}
          showGuestOption={isGuestEntryAllowed(orgPolicies)}
          cachedSessionOffer={authSessionOffer}
          onContinueWithCachedSession={restoreCachedAuthenticatedUser}
        />
      )}

      {screen === SCREEN.ORG_SETUP && (
        <OrgSetupScreen
          userEmail={userEmail}
          onComplete={handleOrgSetupComplete}
          onSignOut={handleSignOutFromOrgSetup}
        />
      )}

      {screen === SCREEN.UPDATE_PASSWORD && (
        <UpdatePasswordScreen
          userEmail={userEmail}
          onComplete={handlePasswordUpdateComplete}
          onBackToSignIn={handlePasswordRecoveryBackToSignIn}
        />
      )}

      {screen === SCREEN.EMAIL_CONFIRMATION && (
        <EmailConfirmationScreen
          email={pendingConfirmEmail}
          variant={confirmVariant}
          onBackToSignIn={handleBackToSignIn}
        />
      )}

      {screen === SCREEN.MODE_SELECT && (
        <ModeSelectScreen
          onSelectMode={handleModeSelect}
          userEmail={userEmail}
          onOpenDashboard={() => setScreen(SCREEN.KPI_DASHBOARD)}
        />
      )}

      {screen === SCREEN.PROJECT_DETAIL && (
        <ProjectDetailScreen
          key={`${selectedProject?.project_id || "project"}:${projectDetailTarget ? `${projectDetailTarget.kind}:${projectDetailTarget.id}` : ""}`}
          project={selectedProject}
          session={session}
          currentUser={currentUser}
          initialTarget={projectDetailTarget}
          returnLabel={appScreenReturnLabel(projectDetailReturnScreen)}
          onOpenTimeLog={
            can(currentUser, CAPABILITY.TIMELOG_VIEW) ? openTimeLogFromProject : undefined
          }
          onOpenProjectTasks={(projectId) => {
            openProjectDetail(
              { project_id: projectId },
              projectDetailReturnScreen,
              { kind: "tasks" },
            );
          }}
          onBack={() => {
            setProjectDetailTarget(null);
            setScreen(projectDetailReturnScreen);
          }}
          onEditProject={openProjectEdit}
          onGenerateInvoice={
            can(currentUser, CAPABILITY.INVOICE_VIEW)
              ? handleGenerateInvoiceFromProject
              : undefined
          }
          onOpenInvoice={
            can(currentUser, CAPABILITY.INVOICE_VIEW)
              ? (invoice) => handleOpenInvoiceInHub(invoice, SCREEN.PROJECT_DETAIL)
              : undefined
          }
        />
      )}
      {screen === SCREEN.ACCOUNTS_PAYABLE &&
        (opsModuleOverride ? (
          <AccountsPayableDashboard
            currentUser={currentUser}
            onBack={() => setScreen(apReturnScreen)}
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          />
        ) : (
          <OpsDashboardCarouselPage
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          >
            {renderOpsDashboardSummary("accounts_payable", {
              currentUser,
              onOpenAccountsPayable: openAccountsPayableModule,
            })}
          </OpsDashboardCarouselPage>
        ))}
      {screen === SCREEN.ACCOUNTS_RECEIVABLE &&
        (opsModuleOverride ? (
          <AccountsReceivableScreen
            currentUser={currentUser}
            onBack={() => setScreen(arReturnScreen)}
            onOpenInvoice={(invoice) =>
              handleOpenInvoiceInHub(invoice, SCREEN.ACCOUNTS_RECEIVABLE)
            }
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          />
        ) : (
          <OpsDashboardCarouselPage
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          >
            {renderOpsDashboardSummary("accounts_receivable", {
              currentUser,
              onOpenAccountsReceivable: openAccountsReceivableFromOps,
            })}
          </OpsDashboardCarouselPage>
        ))}
      {screen === SCREEN.FINANCIAL_POSITION && (
        <OpsDashboardCarouselPage
          onCarouselNavigate={handleDashboardCarouselNavigate}
          onOpenNavigator={handleOpenDashboardNavigator}
        >
          {renderOpsDashboardSummary("financial_position", {
            currentUser,
            onOpenFinance: () => {
              setFinanceReturnScreen(resolveIaReturnTarget(SCREEN.KPI_DASHBOARD));
              setScreen(SCREEN.FINANCE_DASHBOARD);
            },
          })}
        </OpsDashboardCarouselPage>
      )}
      {screen === SCREEN.FINANCE_DASHBOARD && (
        <FinanceDashboardScreen
          currentUser={currentUser}
          onBack={() => setScreen(financeReturnScreen)}
          onOpenWorkspace={openFinanceWorkspace}
          onOpenAccounts={() => setScreen(SCREEN.FINANCE_ACCOUNTS)}
          onOpenActivity={() => setScreen(SCREEN.FINANCE_TRANSACTIONS)}
          onOpenMoneyIn={() => setScreen(SCREEN.FINANCE_MONEY_IN)}
          onOpenMoneyOut={() => {
            setFinanceFlowReturnScreen(SCREEN.FINANCE_DASHBOARD);
            setScreen(SCREEN.FINANCE_MONEY_OUT);
          }}
          onOpenTransfer={() => setScreen(SCREEN.FINANCE_TRANSFER)}
          onOpenExpected={() => handleOpenInvoiceHubWithStatus("sent")}
        />
      )}
      {screen === SCREEN.FINANCE_ACCOUNTS && (
        <FinanceAccountsScreen
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.FINANCE_DASHBOARD)}
        />
      )}
      {screen === SCREEN.FINANCE_TRANSACTIONS && (
        <FinanceTransactionsScreen
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.FINANCE_DASHBOARD)}
          onOpenMoneyIn={() => setScreen(SCREEN.FINANCE_MONEY_IN)}
          onOpenMoneyOut={() => {
            setFinanceFlowReturnScreen(SCREEN.FINANCE_DASHBOARD);
            setScreen(SCREEN.FINANCE_MONEY_OUT);
          }}
          onOpenTransfer={() => setScreen(SCREEN.FINANCE_TRANSFER)}
        />
      )}
      {screen === SCREEN.FINANCE_MONEY_IN && (
        <FinanceMoneyFlowScreen
          mode="in"
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.FINANCE_DASHBOARD)}
          onSuccess={() => {
            dispatchFinancePositionChanged({ reason: "money_in" });
            setScreen(SCREEN.FINANCE_TRANSACTIONS);
          }}
        />
      )}
      {screen === SCREEN.FINANCE_MONEY_OUT && (
        <FinanceMoneyFlowScreen
          mode="out"
          currentUser={currentUser}
          onBack={() => setScreen(financeFlowReturnScreen)}
          onSuccess={() => {
            dispatchFinancePositionChanged({ reason: "money_out" });
            setScreen(SCREEN.FINANCE_TRANSACTIONS);
          }}
        />
      )}
      {screen === SCREEN.FINANCE_TRANSFER && (
        <FinanceTransferScreen
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.FINANCE_DASHBOARD)}
          onSuccess={() => {
            dispatchFinancePositionChanged({ reason: "transfer" });
            setScreen(SCREEN.FINANCE_TRANSACTIONS);
          }}
        />
      )}
      {screen === SCREEN.MATERIALS && (
        <MaterialsScreen
          key={`materials-${materialsInitialView}`}
          initialView={materialsInitialView}
          onBack={() => setScreen(materialsReturnScreen)}
        />
      )}
      {screen === SCREEN.TASKS && (
        <TaskCatalogScreen
          key={`tasks-${tasksInitialView}`}
          initialView={tasksInitialView}
          onBack={() => setScreen(tasksReturnScreen)}
        />
      )}
      {screen === SCREEN.PLANNING && (
        <PlanningScreen
          savedState={restorePlanning.current ? planningSnapshot.current : null}
          onSaveState={(snapshot) => {
            planningSnapshot.current = snapshot;
            restorePlanning.current = false;
          }}
          onOpenItem={(item, projectRecord) => {
            restorePlanning.current = true;
            // Open the exact record Planning shows, in its existing Project context.
            openProjectDetail(
              projectRecord || { project_id: item.projectId },
              SCREEN.PLANNING,
              item.kind === "project" ? null : item,
            );
          }}
          onBack={() => {
            // Back goes where Planning was opened from (Home card or More).
            setScreen(planningBackTo);
            setPlanningBackTo(SCREEN.MODE_SELECT);
          }}
        />
      )}

      {screen === SCREEN.ESTIMATE_HUB && (
        <EstimateHubScreen
          key={`estimate-${estimateHubKey}-${createEstimateInitialView}`}
          initialView={createEstimateInitialView}
          onBack={() => {
            setEditEstimateId(null);
            setEstimateHubInitialStatusFilter(null);
            setScreen(estimateReturnScreen);
          }}
          currentUser={currentUser}
          editEstimateId={editEstimateId}
          initialStatusFilter={estimateHubInitialStatusFilter}
          onCreateProjectFromEstimate={(context) => {
            setEditProjectId(null);
            setCreateProjectInitialView("form");
            setCreateProjectFromEstimate(context);
            setEditEstimateId(context.estimateId);
            setProjectsHubKey((key) => key + 1);
            setScreen(SCREEN.CREATE_PROJECT);
          }}
          onOpenLinkedProject={({ projectId, projectName }) => {
            openProjectDetail(
              { project_id: projectId, project_name: projectName },
              SCREEN.ESTIMATE_HUB,
            );
          }}
        />
      )}

      {screen === SCREEN.INVOICE_HUB && (
        <InvoiceHubScreen
          key={`invoice-${invoiceHubInitialView}-${editInvoiceId || "list"}-${invoiceHubInitialStatusFilter || "any"}`}
          onBack={() => {
            setEditInvoiceId(null);
            setInvoiceHubInitialStatusFilter(null);
            setInvoiceHubInitialView("hub");
            setScreen(invoiceReturnScreen || SCREEN.MODE_SELECT);
          }}
          currentUser={currentUser}
          editInvoiceId={editInvoiceId}
          initialStatusFilter={invoiceHubInitialStatusFilter}
          initialView={invoiceHubInitialView}
        />
      )}

      {screen === SCREEN.START && (
        <StartScreen
          onStart={handleStart}
          isLoading={isLoading}
          onHome={handleNavHome}
        />
      )}

      {screen === SCREEN.QUESTION && (
        <QuestionScreen
          question={currentQuestion}
          questionIndex={questionIndex}
          isLoading={isLoading}
          onSubmitAnswer={handleSubmitAnswer}
          onReviewAnswers={handleOpenReview}
          collectedAnswers={collectedAnswers}
          onHome={handleNavHome}
          onBack={handleNavBack}
          error={error}
          onClearError={clearError}
        />
      )}

      {screen === SCREEN.REVIEW && (
        <ReviewScreen
          collectedAnswers={collectedAnswers}
          onBack={handleCloseReview}
          onEditAnswer={handleEditAnswer}
        />
      )}

      {screen === SCREEN.COMPLETE && (
        <CompletionScreen
          onRestart={handleRestart}
          collectedAnswers={collectedAnswers}
          onHome={handleNavHome}
        />
      )}

      {screen === SCREEN.CREWPILOT && (
        <CockpitHubScreen
          session={session}
          currentUser={currentUser}
          onOpenRequest={() => setScreen(SCREEN.REQUEST)}
          onOpenManualEntry={() => openTimeLogsHub(SCREEN.CREWPILOT)}
          onOpenCreateClient={openClientsHub}
          onOpenCreateProject={openProjectsHub}
          onOpenTimeTrackingV2={() => setScreen(SCREEN.TIME_TRACKING_V2)}
          onOpenTimeEntry={() => {
            devLog("Time Entry");
          }}
          onBack={() => setScreen(SCREEN.MODE_SELECT)}
          onSignOut={handleSignOut}
        />
      )}

      {screen === SCREEN.REQUEST && (
        <RequestScreen
          session={session}
          currentUser={currentUser}
          onBackHome={() => setScreen(SCREEN.CREWPILOT)}
        />
      )}

      {screen === SCREEN.CREATE_CLIENT &&
        (opsModuleOverride ? (
          <CreateClientScreen
            key={`client-${clientsHubKey}-${createClientInitialView}`}
            initialView={createClientInitialView}
            onBack={() =>
              setScreen(
                resolveTopLevelHubReturn(clientReturnScreen, {
                  dashboard: SCREEN.KPI_DASHBOARD,
                  legacyCommandCenter: SCREEN.CREWPILOT,
                }),
              )
            }
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
            onOpenProjectDetail={(project) =>
              openProjectDetail(project, SCREEN.CREATE_CLIENT)
            }
            onOpenInvoice={(invoice) =>
              handleOpenInvoiceInHub(invoice, SCREEN.CREATE_CLIENT)
            }
            onOpenProjectsForClient={openProjectsForClient}
            onOpenTimeLogsForClient={(client, activeProjects = []) => {
              const first = activeProjects?.[0];
              openTimeLogsHub(SCREEN.CREATE_CLIENT, {
                projectId: first?.projectId || first?.project_id || null,
                projectName: first?.name || first?.project_name || null,
              });
            }}
          />
        ) : (
          <OpsDashboardCarouselPage
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          >
            {renderOpsDashboardSummary("create_client", {
              currentUser,
              onOpenClients: openClientsHub,
            })}
          </OpsDashboardCarouselPage>
        ))}

      {screen === SCREEN.MISSION_CONTROL && isMissionControlEnabled() && (
        <MissionControlScreen
          onBack={() => setScreen(SCREEN.MODE_SELECT)}
          onCarouselNavigate={handleDashboardCarouselNavigate}
          onOpenNavigator={handleOpenDashboardNavigator}
        />
      )}

      {screen === SCREEN.CREATE_PROJECT &&
        (opsModuleOverride ? (
          <CreateProjectScreen
            key={
              editProjectId ??
              createProjectFromEstimate?.estimateId ??
              `project-${projectsHubKey}-${createProjectInitialView}-${projectsInitialClientId || "all"}-${projectsHubAttentionFilter || "active"}`
            }
            currentUser={currentUser}
            initialView={createProjectInitialView}
            initialClientId={projectsInitialClientId}
            initialHubFilter={projectsHubAttentionFilter}
            voiceWorkflow={pendingVoiceWorkflow}
            onVoiceWorkflowConsumed={handleVoiceWorkflowConsumed}
            onBack={() => {
              setEditProjectId(null);
              setProjectsInitialClientId("");
              const returnScreen = projectHubBackTarget({
                fromEstimate: Boolean(createProjectFromEstimate),
                returnScreen: projectReturnScreen,
                estimateHub: SCREEN.ESTIMATE_HUB,
                dashboard: SCREEN.KPI_DASHBOARD,
                legacyCommandCenter: SCREEN.CREWPILOT,
              });
              setCreateProjectFromEstimate(null);
              setScreen(returnScreen);
            }}
            onOpenProjectDetail={(project) => {
              setCreateProjectFromEstimate(null);
              setEditProjectId(null);
              setProjectsInitialClientId("");
              setCreateProjectInitialView("hub");
              openProjectDetail(project, SCREEN.CREATE_PROJECT);
            }}
            editProjectId={editProjectId}
            onEditFinished={handleProjectEditFinished}
            fromEstimate={createProjectFromEstimate}
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          />
        ) : (
          <OpsDashboardCarouselPage
            onCarouselNavigate={handleDashboardCarouselNavigate}
            onOpenNavigator={handleOpenDashboardNavigator}
          >
            {renderOpsDashboardSummary("create_project", {
              currentUser,
              onOpenProjects: openProjectsHub,
            })}
          </OpsDashboardCarouselPage>
        ))}

      {screen === SCREEN.MANUAL_TIME_ENTRY && (
        <ManualTimeEntryScreen
          key={`time-logs-${timeLogsHubKey}`}
          session={session}
          currentUser={currentUser}
          onBack={
            timeLogsEditTarget
              ? returnFromTimeLogToProject
              : () =>
                  setScreen(
                    resolveTopLevelHubReturn(timeLogsReturnScreen, {
                      dashboard: SCREEN.KPI_DASHBOARD,
                      legacyCommandCenter: SCREEN.CREWPILOT,
                    }),
                  )
          }
          initialEditLogId={timeLogsEditTarget?.logId ?? null}
          onReturnToCaller={timeLogsEditTarget ? returnFromTimeLogToProject : null}
          initialProjectId={timeLogsInitialProject?.projectId ?? null}
          initialProjectName={timeLogsInitialProject?.projectName ?? null}
          initialDateRangeFilter={timeLogsInitialDateRangeFilter}
          initialView={timeLogsInitialView}
        />
      )}
      {screen === SCREEN.TIME_TRACKING_V2 && (
        <TimeTrackingV2Screen
          session={session}
          currentUser={currentUser}
          directReportIds={directReportIds}
          voiceWorkflow={pendingVoiceWorkflow}
          onVoiceWorkflowConsumed={handleVoiceWorkflowConsumed}
          onBack={() => setScreen(SCREEN.KPI_DASHBOARD)}
        />
      )}

      {screen === SCREEN.QUICK_CREATE && (
        <QuickCreateScreen
          onSwipe={handleDashboardCarouselNavigate}
          onAction={handleQuickCreateAction}
        />
      )}

      {screen === SCREEN.KPI_DASHBOARD && (
        <KPIDashboardScreen
          userName={userName}
          currentUser={currentUser}
          onOpenUserPreferences={() => openSettings("overview")}
          onOpenAshleyHome={() => setScreen(SCREEN.ASHLEY_HOME)}
          onOpenProjectHoursRow={(project) => {
            if (isEmployee(currentUser)) {
              openTimeLogsHub(SCREEN.KPI_DASHBOARD, {
                projectId: project.project_id,
                projectName: project.name,
              });
              return;
            }

            openProjectDetail(project, SCREEN.KPI_DASHBOARD);
          }}
          onOpenProjectHours={() => {
            if (isEmployee(currentUser)) {
              openTimeLogsHub(SCREEN.KPI_DASHBOARD);
            } else {
              openProjectsHub();
            }
          }}
          onOpenSession={() => setScreen(SCREEN.TIME_TRACKING_V2)}
          onCarouselNavigate={handleDashboardCarouselNavigate}
          onOpenEstimateHubWithStatus={handleOpenEstimateHubWithStatus}
          onOpenFinance={() => {
            setFinanceReturnScreen(resolveIaReturnTarget(SCREEN.KPI_DASHBOARD));
            setScreen(SCREEN.FINANCE_DASHBOARD);
          }}
          onOpenAccountsReceivable={openAccountsReceivableFromOps}
          onOpenOverdueInvoices={openOverdueInvoices}
          onOpenAccountsPayable={openAccountsPayableModule}
          onOpenClients={openClientsHub}
          onOpenProjects={openProjectsHub}
          onOpenProjectsWithOverdueTasks={openProjectsWithOverdueTasks}
          onOpenTimeLogs={() => openTimeLogsHub(SCREEN.KPI_DASHBOARD)}
          onOpenTimeLog={openTimeLogFromDashboard}
          onOpenTimeLogsThisWeek={() =>
            openTimeLogsHub(SCREEN.KPI_DASHBOARD, {
              dateRangeFilter: "this_week",
            })
          }
          onOpenPlanning={() => {
            setPlanningBackTo(SCREEN.KPI_DASHBOARD);
            setScreen(SCREEN.PLANNING);
          }}
          quickActions={[
            { id: "client", label: "New Client", icon: "user", tone: "gold", capability: CAPABILITY.CLIENT_CREATE, onClick: openNewClient },
            { id: "project", label: "New Project", icon: "folder", tone: "gold", capability: CAPABILITY.PROJECT_CREATE, onClick: openNewProject },
            { id: "estimate", label: "New Estimate", icon: "doc", tone: "blue", capability: CAPABILITY.ESTIMATE_EDIT, onClick: () => openNewEstimate(SCREEN.KPI_DASHBOARD) },
            { id: "invoice", label: "Create Invoice", icon: "doc", tone: "green", capability: CAPABILITY.SCREEN_INVOICE_HUB, onClick: () => handleModeSelect("invoices") },
            { id: "time", label: "Log Time", icon: "clock", tone: "purple", capability: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY, onClick: () => openTimeLogsHub(SCREEN.KPI_DASHBOARD) },
            { id: "material", label: "Add Material", icon: "box", tone: "orange", capability: CAPABILITY.SCREEN_MATERIALS, onClick: () => handleModeSelect("materials") },
            { id: "planning", label: "Open Planning", icon: "calendar", tone: "blue", capability: CAPABILITY.SCREEN_PLANNING, onClick: () => handleModeSelect("planning") },
            { id: "ashley", label: "Build with Ashley", icon: "spark", tone: "purple", onClick: () => setScreen(SCREEN.ASHLEY_HOME) },
          ]}
          onOpenGoogleVisibility={() => setScreen(SCREEN.GOOGLE_VISIBILITY)}
        />
      )}

      {screen === SCREEN.PLAY_STORE_READINESS && (
        <PlayStoreReadinessRetired onBack={() => setScreen(SCREEN.KPI_DASHBOARD)} />
      )}

      {screen === SCREEN.GOOGLE_VISIBILITY && (
        <GoogleVisibilityScreen
          actor={currentUser}
          onBack={() => setScreen(SCREEN.KPI_DASHBOARD)}
        />
      )}

      {screen === SCREEN.ASHLEY_HOME && (
        <AshleyHomeScreen onBack={() => setScreen(SCREEN.KPI_DASHBOARD)} />
      )}

      {screen === SCREEN.ESTIMATE_PIPELINE && (
        <OpsDashboardCarouselPage
          onCarouselNavigate={handleDashboardCarouselNavigate}
          onOpenNavigator={handleOpenDashboardNavigator}
        >
          {renderOpsDashboardSummary("estimate_pipeline", {
            currentUser,
            onOpenEstimateHub: () =>
              handleOpenEstimateHubWithStatus(null, SCREEN.ESTIMATE_PIPELINE),
          })}
        </OpsDashboardCarouselPage>
      )}
      {screen === SCREEN.OPEN_SESSION && (
        <OpenSessionScreen
          onBack={() => setScreen(SCREEN.KPI_DASHBOARD)}
          onStopSession={() => setScreen(SCREEN.CREWPILOT)}
        />
      )}

      {/* ── Intelligence module ─────────────────────────── */}
      {screen === SCREEN.INTELLIGENCE && (
        <IntelligenceScreen
          onOpenHoursReports={() => setScreen(SCREEN.REPORT_BUILDER)}
          onOpenPayrollReports={() => setScreen(SCREEN.PAYROLL_REPORT)}
          onOpenProjectAnalytics={() => setScreen(SCREEN.PROJECT_ANALYTICS)}
          onBack={() => setScreen(SCREEN.MODE_SELECT)}
          currentUser={currentUser}
        />
      )}
      {screen === SCREEN.PROJECT_ANALYTICS && (
        <ProjectAnalyticsScreen
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.INTELLIGENCE)}
          onOpenProjectDetail={(project) =>
            openProjectDetail(project, SCREEN.PROJECT_ANALYTICS)
          }
        />
      )}
      {screen === SCREEN.PAYROLL_HOME && (
        <PayrollHomeScreen
          currentUser={currentUser}
          onBack={() => setScreen(payrollHomeReturnScreen)}
          onOpenQueue={() => {
            setPayrollReturnScreen(SCREEN.PAYROLL_HOME);
            setScreen(SCREEN.PAYROLL_QUEUE);
          }}
        />
      )}
      {screen === SCREEN.PAYROLL_QUEUE && (
        <PayrollQueueScreen
          currentUser={currentUser}
          onBack={() => setScreen(payrollReturnScreen)}
        />
      )}
      {screen === SCREEN.PAYROLL_REPORT && (
        <PayrollReportScreen
          currentUser={currentUser}
          onBack={() => setScreen(SCREEN.INTELLIGENCE)}
        />
      )}
      {screen === SCREEN.PAYROLL_PRINT_ISOLATION && (
        <PayrollPrintIsolationTest
          onBack={() => {
            window.location.hash = "";
            setScreen(SCREEN.MODE_SELECT);
          }}
        />
      )}
      {screen === SCREEN.REPORT_BUILDER && (
        <ReportBuilderScreen
          onBack={() => setScreen(SCREEN.INTELLIGENCE)}
          currentUser={currentUser}
        />
      )}
      {(screen === SCREEN.ADMINISTRATION ||
        screen === SCREEN.USER_MANAGEMENT) && (
        <AdministrationHubScreen
          onBack={() => setScreen(isDesktopViewport ? SCREEN.KPI_DASHBOARD : SCREEN.MODE_SELECT)}
          currentUser={currentUser}
          initialSection={
            screen === SCREEN.USER_MANAGEMENT
              ? "users"
              : adminInitialSection
          }
        />
      )}
      {screen === SCREEN.USER_PREFERENCES && (
        <UserPreferencesScreen
          onBack={() => setScreen(SCREEN.KPI_DASHBOARD)}
          onSignOut={handleSignOut}
        />
      )}
      {screen === SCREEN.FEEDBACK_INBOX && (
        <FeedbackInboxScreen
          actor={currentUser}
          onBack={() => setScreen(isDesktopViewport ? SCREEN.KPI_DASHBOARD : SCREEN.MODE_SELECT)}
        />
      )}
      {screen === SCREEN.FEEDBACK && (
        <FeedbackScreen
          onBack={closeFeedback}
          backLabel={
            feedbackReturnScreen === SCREEN.MODE_SELECT
              ? "More"
              : feedbackReturnScreen === SCREEN.KPI_DASHBOARD
                ? "Home"
                : feedbackReturnScreen === SCREEN.TIME_TRACKING_V2
                  ? "Time"
                  : appScreenReturnLabel(feedbackReturnScreen)
          }
          organizationId={resolveOrganizationIdFromActor(currentUser)}
          accountEmail={userEmail || currentUser?.email || ""}
          sourceRoute={feedbackSourceRoute}
          sourceScreen={feedbackSourceScreen}
        />
      )}
      {screen === SCREEN.SETTINGS && (
        <SettingsHubScreen
          onBack={() => setScreen(settingsReturnScreen || SCREEN.KPI_DASHBOARD)}
          currentUser={currentUser}
          onSignOut={handleSignOut}
          initialSection={settingsInitialSection}
        />
      )}
      </AppDesktopShell>
      {guideBriefing && (
        <>
          <video
            autoPlay
            playsInline
            controls={false}
            muted={false}
            onEnded={() => {
              setGuideBriefing(false);
            }}
            onClick={() => {
              setGuideBriefing(false);
            }}
            style={{
              position: "fixed",
              top: "15px",
              right: "5px",
              width: "140px",
              height: "140px",
              zIndex: 99999,
              cursor: "pointer",
              pointerEvents: "auto",
              objectFit: "cover",
              clipPath: "circle(42% at 50% 50%)",
            }}
          >
            <source src={ashleyGuideVideo} type="video/mp4" />
          </video>

          <img
            src={ringPng}
            alt="Ashley Ring"
            style={{
              position: "fixed",
              top: "2px",
              right: "-2px",
              width: "155px",
              height: "155px",
              zIndex: 100000,
              pointerEvents: "none",
            }}
          />
        </>
      )}
      {startupBriefing && (
        <div
          style={{
            pointerEvents: "none",
          }}
        >
          <video
            autoPlay
            playsInline
            controls={false}
            muted
            onClick={() => {
              window.speechSynthesis.cancel();
              setStartupBriefing(false);
            }}
            onEnded={() => {
              setStartupBriefing(false);
            }}
            style={{
              position: "fixed",
              top: "15px",
              right: "5px",
              width: "140px",
              height: "140px",
              zIndex: 99999,
              pointerEvents: "auto",
              cursor: "pointer",
              objectFit: "cover",
              clipPath: "circle(42% at 50% 50%)",
            }}
          >
            <source src={ashleyUpdatesVideo} type="video/mp4" />
          </video>

          <img
            src={ringPng}
            alt="Ashley Ring"
            style={{
              position: "fixed",
              top: "2px",
              right: "-2px",
              width: "155px",
              height: "155px",
              zIndex: 100000,
              pointerEvents: "none",

              filter: `
          drop-shadow(0 0 4px #3b82f6)
          drop-shadow(0 0 8px #2563eb)
        `,
            }}
          />
        </div>
      )}

      {isDashboardCarouselScreen(screen) && (
        <DashboardNavigator
          open={dashboardNavigatorOpen}
          currentScreen={screen}
          onClose={handleCloseDashboardNavigator}
          onNavigate={handleDashboardDirectNavigate}
        />
      )}

      <FeedbackLauncher
        hidden={
          !currentUser ||
          isGuestActor(currentUser) ||
          screen === SCREEN.FEEDBACK ||
          screen === SCREEN.FEEDBACK_INBOX ||
          screen === SCREEN.ONBOARDING ||
          screen === SCREEN.EMAIL_CONFIRMATION ||
          screen === SCREEN.UPDATE_PASSWORD ||
          screen === SCREEN.ORG_SETUP ||
          screen === SCREEN.PAYROLL_PRINT_ISOLATION
        }
        onClick={openFeedback}
      />

      <MobileBottomNav
        hidden={
          screen === SCREEN.ONBOARDING ||
          screen === SCREEN.EMAIL_CONFIRMATION ||
          screen === SCREEN.UPDATE_PASSWORD ||
          screen === SCREEN.PAYROLL_PRINT_ISOLATION
        }
        activeTab={
          screen === SCREEN.KPI_DASHBOARD || screen === SCREEN.QUICK_CREATE
            ? "home"
            : screen === SCREEN.TIME_TRACKING_V2
              ? "time"
              : screen === SCREEN.MODE_SELECT
                ? "more"
                : undefined
        }
        onOpenDashboard={() => setScreen(SCREEN.KPI_DASHBOARD)}
        onOpenProjects={openProjectsHub}
        onOpenTime={() => setScreen(SCREEN.TIME_TRACKING_V2)}
        onOpenMore={() => setScreen(SCREEN.MODE_SELECT)}
      />

      <FloatingHomeButton
        hidden={
          screen === SCREEN.ONBOARDING ||
          screen === SCREEN.EMAIL_CONFIRMATION ||
          screen === SCREEN.UPDATE_PASSWORD ||
          screen === SCREEN.PAYROLL_PRINT_ISOLATION
        }
        favoriteIds={getQuickMenuFavoriteIdsForActor(currentUser)}
        showPayrollQuick={
          isEmployee(currentUser) ||
          can(currentUser, CAPABILITY.PAYROLL_QUEUE)
        }
        showEstimatesQuick={can(currentUser, CAPABILITY.SCREEN_ESTIMATE_HUB)}
        showTimeLogsQuick={isEmployee(currentUser)}
        showCreateProjectQuick={can(currentUser, CAPABILITY.PROJECT_VIEW)}
        onOpenModeSelect={() => setScreen(SCREEN.MODE_SELECT)}
        onOpenDashboard={() => setScreen(SCREEN.KPI_DASHBOARD)}
        onOpenEstimates={() => handleOpenEstimateHubWithStatus(null, screen)}
        onOpenPayrollQueue={() => {
          setPayrollReturnScreen(
            screen === SCREEN.PAYROLL_HOME ? SCREEN.PAYROLL_HOME : screen,
          );
          setScreen(SCREEN.PAYROLL_QUEUE);
        }}
        onOpenCommandCenter={() => setScreen(SCREEN.TIME_TRACKING_V2)}
        onOpenTimeLogs={() => openTimeLogsHub(SCREEN.KPI_DASHBOARD)}
        onOpenCreateProject={openProjectsHub}
      />

      {abandonedMaterialDrafts.length > 0 && (
        <AbandonedSessionMaterialsRecovery
          drafts={abandonedMaterialDrafts}
          onResolved={(remaining) => {
            setAbandonedMaterialDrafts(remaining || []);
          }}
          onDismiss={() => setAbandonedMaterialDrafts([])}
        />
      )}

      <AshleyVoiceOverlay />
      <AshleyVoiceDebugPanel />
      <WhatsNewHost actor={currentUser} authLoading={authLoading} />
    </div>
    </AshleyInterfaceProvider>
    </ProductEducationProvider>
    </HardwareBackProvider>
    </AppProviders>
  );
}
